import type { EnTranslations } from '@/i18n/locales/en';

type TranslationTree<T> = {
  [Key in keyof T]: T[Key] extends string ? string : TranslationTree<T[Key]>;
};

export const ar: TranslationTree<EnTranslations> = {
  common: {
    cancel: 'إلغاء',
    save: 'حفظ',
    done: 'تم',
    saving: 'جارٍ الحفظ…',
    loading: 'جارٍ التحميل…',
    error: 'خطأ',
    close: 'إغلاق',
    back: 'رجوع',
    search: 'بحث…',
    english: 'English',
    arabic: 'العربية',
    language: 'اللغة',
    comment: 'تعليق',
    everyone: 'الجميع',
    student_one: 'طالب واحد',
    student_other: '{{count}} طالب',
    allMembers: 'جميع الأعضاء',
    selectMembers: 'اختر الأعضاء',
    selected_one: 'تم اختيار {{count}}',
    selected_other: 'تم اختيار {{count}}',
    recipient_one: 'مستلم واحد',
    recipient_other: '{{count}} مستلم',
    managementFootnote: 'سيظل مديرو الصف ومعلّموه يتلقون هذا المنشور.',
    noIndividualRecipients:
      'لا يمكن اختيار مستلمين بشكل فردي لهذا الصف — سيُرسل المنشور إلى جميع الأعضاء.',
    selectAtLeastOne: 'يرجى اختيار طالب واحد على الأقل أو اختيار جميع الأعضاء.',
    you: 'أنت',
    backToHome: 'العودة إلى الرئيسية',
    saveChanges: 'حفظ التغييرات',
    confirmDelete: 'تأكيد الحذف',
    optional: 'اختياري',
    today: 'اليوم',
    yesterday: 'أمس',
    previous: 'السابق',
    next: 'التالي',
    selectDate: 'اختر التاريخ',
    datePickerAria: 'منتقي التاريخ',
    previousMonth: 'الشهر السابق',
    nextMonth: 'الشهر التالي',
    previousYears: 'السنوات السابقة',
    nextYears: 'السنوات التالية',
    selectMonth: 'اختر الشهر',
    selectYear: 'اختر السنة',
    monthJanuary: 'يناير',
    monthFebruary: 'فبراير',
    monthMarch: 'مارس',
    monthApril: 'أبريل',
    monthMay: 'مايو',
    monthJune: 'يونيو',
    monthJuly: 'يوليو',
    monthAugust: 'أغسطس',
    monthSeptember: 'سبتمبر',
    monthOctober: 'أكتوبر',
    monthNovember: 'نوفمبر',
    monthDecember: 'ديسمبر',
    weekdaySu: 'أح',
    weekdayMo: 'إث',
    weekdayTu: 'ثل',
    weekdayWe: 'أر',
    weekdayTh: 'خم',
    weekdayFr: 'جم',
    weekdaySa: 'سب',
    selectTime: 'اختر الوقت',
    timePickerAria: 'منتقي الوقت',
    hour: 'ساعة',
    minute: 'دقيقة',
    amPm: 'ص/م',
    now: 'الآن',
    timePlaceholder: '—:—',
    select: 'اختر…',
    optionsAria: 'الخيارات',
    noMatchesGeneric: 'لا توجد نتائج',
    somethingWentWrong: 'حدث خطأ ما',
    closeDialogAria: 'إغلاق الحوار',
    errorCode: 'الرمز: {{code}}',
    ok: 'موافق',
    closeMenu: 'إغلاق القائمة',
    download: 'تنزيل',
    reply: 'رد',
    linkCopied: 'تم نسخ الرابط',
    noSubject: 'بدون مادة',
    notFoundDescription: 'الصفحة التي تبحث عنها غير موجودة.',
    goHome: 'العودة للرئيسية',
    premiumMember: 'عضو مميز',
    verified: 'موثّق',
    premiumAccount: 'حساب مميز',
    verifiedAccount: 'حساب موثّق',
  },
  nav: {
    home: 'الرئيسية',
    explore: 'استكشاف',
    messages: 'الرسائل',
    calendar: 'التقويم',
    payments: 'المدفوعات',
    reportCards: 'بطاقات التقرير',
    ownedClasses: 'صفوفي',
    joinedClasses: 'الصفوف المنضم إليها',
    addClass: 'إضافة صف',
    noOwnedClasses: 'لا تملك أي صفوف بعد.',
    noJoinedClasses: 'لم تنضم إلى أي صف بعد.',
    tryPrime: 'جرّب MyU Prime',
    mainNav: 'القائمة الرئيسية',
    openMenu: 'فتح قائمة التنقل',
    closeMenu: 'إغلاق قائمة التنقل',
  },
  userMenu: {
    viewProfile: 'عرض الملف الشخصي',
    shareProfile: 'مشاركة الملف الشخصي',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
  },
  auth: {
    signIn: 'تسجيل الدخول',
    signInSubtitle: 'مرحبًا بعودتك! يرجى إدخال بياناتك',
    emailOrUsername: 'البريد الإلكتروني أو اسم المستخدم',
    emailPlaceholder: 'البريد الإلكتروني أو اسم المستخدم',
    password: 'كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    forgotPassword: 'هل نسيت كلمة المرور؟',
    noAccount: 'ليس لديك حساب؟',
    createAccount: 'إنشاء حساب',
    register: 'التسجيل',
    signingIn: 'جارٍ تسجيل الدخول…',
    verificationLinkNote: 'سيتم إرسال رابط تحقق',
    registerTitle: 'إنشاء حسابك',
    forgotTitle: 'هل نسيت كلمة المرور؟',
    forgotSubtitle: 'أدخل اسم المستخدم أو البريد الإلكتروني وسنرسل تعليمات إعادة التعيين.',
    send: 'إرسال',
    sending: 'جارٍ الإرسال…',
    forgotSuccess: 'يرجى التحقق من بريدك الإلكتروني للحصول على تعليمات إعادة تعيين كلمة المرور.',
    alreadyHaveAccount: 'لديك حساب بالفعل؟',
    logIn: 'تسجيل الدخول',
    continue: 'متابعة',
    closeRegistration: 'إغلاق التسجيل',
    selectAccountType: 'اختر نوع حسابك',
    chooseAccountTypeSubtitle: 'اختر الخيار الذي يصفك بشكل أفضل',
    accountTypeStudent: 'طالب',
    accountTypeInstructor: 'معلّم أو أستاذ',
    accountTypeManagement: 'كادر',
    accountTypeParent: 'ولي أمر',
    managementControlPanelCta: 'هل تسجّل مدرستك للوحة تحكم الإدارة؟',
    managementControlPanelLink: 'الانتقال إلى تسجيل الإدارة',
    findSchool: 'ابحث عن مدرستك',
    findUniversity: 'ابحث عن جامعتك',
    schoolPlaceholder: 'اكتب اسم مدرستك...',
    universityPlaceholder: 'اكتب اسم جامعتك...',
    findInstitutionSubtitle: 'ابحث واختر مؤسستك للبدء',
    educationalInstitution: 'مؤسسة تعليمية',
    noMatches: 'لم يتم العثور على نتائج لـ "{{query}}".',
    suggestionsFailed: 'تعذّر تحميل الاقتراحات. يرجى المحاولة مرة أخرى.',
    keepTyping: 'تابع الكتابة لرؤية الاقتراحات…',
    createProfile: 'أنشئ ملفك الشخصي',
    createProfileSubtitle: 'أخبرنا عن نفسك لإكمال التسجيل',
    firstName: 'الاسم الأول والأوسط',
    lastName: 'اسم العائلة',
    username: 'اسم المستخدم',
    emailAddress: 'البريد الإلكتروني',
    firstNamePlaceholder: 'محمد',
    lastNamePlaceholder: 'أحمد',
    firstNameInvalid: 'يرجى إدخال اسم صالح يحتوي على حرف واحد على الأقل.',
    lastNameInvalid: 'يرجى إدخال اسم صالح يحتوي على حرف واحد على الأقل.',
    usernamePlaceholder: 'mohammedahmed',
    usernameChecking: 'جارٍ التحقق من التوفر…',
    usernameAvailable: 'اسم المستخدم متاح',
    usernameTaken: 'اسم المستخدم هذا مستخدم بالفعل.',
    usernameInvalidFormat: 'استخدم من 3 إلى 30 حرفًا أو رقمًا أو شرطة سفلية.',
    usernameSuggestionsLabel: 'جرّب أحد هذه الأسماء:',
    emailPlaceholderRegister: 'mohammed@university.edu',
    emailChecking: 'جارٍ التحقق من البريد الإلكتروني…',
    emailTaken: 'هذا البريد الإلكتروني مُسجَّل بالفعل.',
    emailInvalidFormat: 'يرجى إدخال بريد إلكتروني صالح.',
    verificationEmailNote: 'سيتم إرسال رابط تحقق إلى بريدك الإلكتروني',
    createStrongPassword: 'أنشئ كلمة مرور قوية',
    creatingAccount: 'جارٍ إنشاء الحساب…',
    agreeTermsPrefix: 'بالتسجيل، فإنك توافق على',
    termsOfService: 'شروط الخدمة',
    and: 'و',
    privacyPolicy: 'سياسة الخصوصية',
    goBack: 'رجوع',
    pushGateHint: 'فعّل الإشعارات أعلاه للمتابعة.',
    usernameOrEmail: 'اسم المستخدم أو البريد الإلكتروني',
    forgotEmailPlaceholder: 'your.email@university.edu',
    resetLinkNote: 'سيتم إرسال رابط إعادة تعيين كلمة المرور إلى البريد المرتبط بحسابك.',
    sendResetLink: 'إرسال رابط إعادة التعيين',
    backToSignIn: 'العودة لتسجيل الدخول',
    forgotValidationRequired: 'يرجى إدخال اسم المستخدم أو البريد الإلكتروني.',
    registerCompleteTitle: 'كل شيء جاهز!',
    verificationSentTo: 'تم إرسال رابط التحقق إلى',
    yourEmailAddress: 'بريدك الإلكتروني',
    verifyEmailPrompt: 'انقر على رابط التحقق في ذلك البريد لتأكيد حسابك.',
    starting: 'جارٍ البدء…',
    start: 'ابدأ',
    pickInstitutionBeforeContinue: 'يرجى اختيار مؤسسة من الاقتراحات قبل المتابعة.',
  },
  settings: {
    title: 'الإعدادات',
    description: 'إدارة حسابك وخصوصيتك وتفضيلات التطبيق.',
    archivedClasses: 'الصفوف المؤرشفة',
    blockedUsers: 'المستخدمون المحظورون',
    storageTitle: 'مساحة التخزين',
    storageDescription: 'تُحتسب المستندات ومقاطع الفيديو المخزنة على MyU ضمن حدك.',
    storageUsed: '{{used}} من {{limit}} مستخدم',
    storageAria: 'التخزين المستخدم',
    network: 'الشبكة',
    connected: 'متصل',
    connecting: 'جارٍ الاتصال…',
    offline: 'غير متصل',
    prime: 'MyU Prime',
    primeActive: 'نشط حتى {{date}}',
    primeInactive: 'قم بالترقية لمزيد من التخزين والميزات',
    about: 'حول',
    version: 'الإصدار',
    contact: 'تواصل معنا',
    privacy: 'الخصوصية والظهور',
    notifications: 'الإشعارات',
    account: 'الحساب',
    pushNotifications: 'الإشعارات الفورية',
    pushNotificationsDescription: 'اختر النشاط الذي يرسل تنبيهات فورية.',
    privacyDescription: 'تحكم في من يمكنه رؤيتك والتفاعل معك.',
    accountLegal: 'الحساب والشؤون القانونية',
    accountLegalDescription: 'حول MyU والسياسات وإجراءات الحساب.',
    contactUs: 'تواصل معنا',
    networkStatus: 'حالة الشبكة',
    active: 'نشط',
    activeUntil: 'نشط حتى {{date}}',
    viewPrime: 'عرض MyU Prime',
    upgradePrime: 'الترقية إلى MyU Prime',
    upgradePrimeDescription: 'قم بالترقية للحصول على {{gb}} GB من التخزين ودعم أولوية والمزيد.',
    backToSettings: 'العودة إلى الإعدادات',
    privacyPageDescription: 'تحكم في خصوصية حسابك وكيفية تفاعل الآخرين معك.',
    privacyIntro:
      'تنطبق هذه الإعدادات على MyU بالكامل. لا يزال بإمكانك تغيير الخيارات لكل منشور عند النشر.',
    privateAccount: 'حساب خاص',
    privateAccountDescription:
      'عندما يكون حسابك خاصًا، يجب أن توافق على طلبات المتابعة قبل أن يرى أي شخص منشوراتك.',
    showOnExplore: 'الظهور في صفحة الاستكشاف',
    showOnExploreDescription:
      'عند التفعيل، قد يظهر ملفك الشخصي في صفحة الاستكشاف. الترتيب يعتمد على التفاعل مع حسابك.',
    receiveComments: 'تلقي التعليقات',
    receiveCommentsDescription:
      'الإعداد الافتراضي للمنشورات الجديدة. لا يزال بإمكانك السماح بالتعليقات أو منعها لكل منشور بعد النشر.',
    allowDownloads: 'السماح بالتنزيلات',
    allowDownloadsDescription:
      'تحكم فيما إذا كان بإمكان المستخدمين الآخرين تنزيل الصور ومقاطع الفيديو والملفات التي رفعتها.',
    messageFromLegend: 'تلقي رسائل خاصة من',
    messageFromAnyone: 'أي شخص على MyU',
    messageFromSchoolAndConnections: 'داخل مؤسستي، والحسابات التي أتابعها أو أشارك صفًا معها',
    messageFromConnectionsOnly: 'فقط الحسابات التي أتابعها أو أشارك صفًا معها',
    messageFromNoOne: 'لا أحد',
    pushNotificationsPageDescription: 'اختر الإشعارات التي تُرسل إلى أجهزتك.',
    pushNewPosts: 'منشورات جديدة',
    pushNewPostsDescription: 'عندما ينشر شخص تتابعه منشورًا جديدًا.',
    pushLikes: 'إعجابات',
    pushLikesDescription: 'عندما يعجب شخص بمنشورك أو تعليقك.',
    pushComments: 'تعليقات',
    pushCommentsDescription: 'عندما يعلق شخص على منشوراتك.',
    pushMessages: 'رسائل',
    pushMessagesDescription: 'رسائل مباشرة جديدة ونشاط في الدردشات الجماعية.',
    pushFollowing: 'متابعة',
    pushFollowingDescription: 'عندما يتابعك شخص أو يقبل طلب متابعتك.',
    pushClassJoins: 'انضمام للصفوف',
    pushClassJoinsDescription: 'عندما ينضم أعضاء إلى الصفوف التي تديرها.',
    accountLegalPageDescription: 'تعرّف على MyU وأدر حالة حسابك.',
    aboutUs: 'من نحن',
    privacyPolicyLink: 'سياسة الخصوصية',
    accountActions: 'إجراءات الحساب',
    disableAccount: 'تعطيل الحساب',
    disableAccountDescription:
      'إخفاء ملفك الشخصي مؤقتًا وإيقاف الإشعارات. يمكنك تسجيل الدخول مجددًا في أي وقت لإعادة التفعيل.',
    enableAccount: 'تفعيل الحساب',
    enableAccountDescription: 'إعادة تفعيل ملفك الشخصي واستئناف الوصول الطبيعي إلى الحساب.',
    deleteAccount: 'حذف الحساب',
    deleteAccountDescription: 'حذف حسابك من MyU. سيتم تسجيل خروجك بعد نجاح الطلب.',
    disableAccountConfirm: 'تعطيل الحساب؟',
    enableAccountConfirm: 'تفعيل الحساب؟',
    deleteAccountConfirm: 'حذف الحساب؟',
    disableAccountDemoNote:
      'سيُخفى ملفك الشخصي حتى يُعاد تفعيل حسابك. سيتم تسجيل خروجك بعد تعطيله.',
    enableAccountDemoNote: 'سيُستعاد ملفك الشخصي والوصول إلى الحساب.',
    deleteAccountDemoNote: 'سيتم إرسال طلب حذف حسابك الآن. سيتم تسجيل خروجك بعد نجاحه.',
    accountActionFailed: 'تعذّر تحديث حسابك. يرجى المحاولة مرة أخرى.',
    accountActionSignInRequired: 'يرجى تسجيل الدخول مرة أخرى قبل تغيير حالة حسابك.',
    disable: 'تعطيل',
    enable: 'تفعيل',
    deletePermanently: 'حذف الحساب',
    blockedUsersDescription: 'لا يمكن للمستخدمين المحظورين مراسلتك أو متابعتك أو رؤية ملفك الشخصي.',
    blockedUsersLoadError: 'تعذّر تحميل المستخدمين المحظورين',
    connectionError: 'يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
    noBlockedUsers: 'لا يوجد مستخدمون محظورون',
    noBlockedUsersDescription: 'عند حظر شخص، سيظهر هنا ويمكنك إلغاء الحظر في أي وقت.',
    unblock: 'إلغاء الحظر',
    archivedClassesDescription: 'انقر على صف لفتحه، أو استخدم تعديل لاستعادته أو تحديث تفاصيله.',
    archivedClassesLoadError: 'تعذّر تحميل الصفوف المؤرشفة',
    noArchivedClasses: 'لا توجد صفوف مؤرشفة',
    noArchivedClassesDescription:
      'عند أرشفة صف، سيظهر هنا. افتح الصف لعرضه، أو عدّله لاستعادته أو إجراء تغييرات.',
  },
  post: {
    editCaption: 'تعديل النص',
    editRecipients: ' تعديل المستلمين',
    disableComments: 'تعطيل التعليقات',
    enableComments: 'تفعيل التعليقات',
    pinToClass: 'تثبيت في الصف',
    pinToProfile: 'تثبيت في الملف الشخصي',
    unpinFromClass: 'إلغاء التثبيت من الصف',
    unpinFromProfile: 'إلغاء التثبيت من الملف الشخصي',
    deletePost: 'حذف المنشور',
    postOptions: 'خيارات المنشور',
    editRecipientsTitle: 'تعديل المستلمين',
    editRecipientsIntro: 'اختر من يتلقى هذا المنشور في {{className}}.',
    loadingRoster: 'جارٍ تحميل القائمة…',
    recipientsLabel: 'المستلمون: {{label}}',
    likesAria_one: 'إعجاب واحد. عرض من أعجب',
    likesAria_other: '{{count}} إعجاب. عرض من أعجب',
    commentsAria: '{{count}} تعليق. عرض التعليقات',
    commentsDisabledAria: '{{count}} تعليق. عرض التعليقات (التعليقات الجديدة معطّلة)',
    impressionsAria_one: 'مشاهدة واحدة. عرض إحصاءات المنشور',
    impressionsAria_other: '{{count}} مشاهدة. عرض إحصاءات المنشور',
    impressionsReadOnly: '{{count}} مشاهدة',
    inClass: 'في {{className}}',
    pinned: 'مثبّت',
    promoted: 'مروّج',
    newCommentsDisabled: 'التعليقات الجديدة معطّلة',
    detailModalAriaLabel: 'المنشور والتعليقات',
    detailModalTitle: 'منشور',
    commentCount_one: '{{count}} تعليق',
    commentCount_other: '{{count}} تعليق',
    commentsDisabledWithExisting: 'التعليقات الجديدة معطّلة على هذا المنشور.',
    commentsDisabledEmpty: 'التعليقات معطّلة على هذا المنشور.',
    editCaptionPlaceholder: 'اكتب نصًا… استخدم #وسوم أو @إشارات',
    pinnedToClass: 'مثبّت في {{className}}',
    pinnedToProfile: 'مثبّت في الملف الشخصي',
    questionBannerLabel: 'سؤال',
    questionGradedLine: 'مُقيّم · {{termName}} · {{categoryName}} · {{totalGrade}} نقطة',
    extendDueDate: 'تمديد تاريخ الاستحقاق',
    extendDueDateDescription: 'يحدّث تاريخ الاستحقاق لجميع نسخ هذا السؤال المرسلة في نفس الدفعة.',
    newDueDate: 'تاريخ الاستحقاق الجديد',
    newDueTime: 'وقت الاستحقاق الجديد',
    questionOptionsAria: 'خيارات السؤال',
    dueOn: 'مستحق في',
    editBeforeDueFootnote: 'يمكن للمستلمين تعديل ردودهم قبل تاريخ الاستحقاق.',
    submitError: 'فشل إرسال الرد. يرجى المحاولة مرة أخرى.',
    submitAlreadyGradedError: 'تم تقييم ردك بالفعل. عرض ردك لتشاهد تقييمك.',
    reviewResponses: 'مراجعة الردود',
    reviewResponsesCount: 'مراجعة الردود ({{count}})',
    viewYourResponse: 'عرض ردك',
    responseRejectedCtaHint: 'تم رفض الرد - عرض التفاصيل',
    responseSubmittedGradeCtaHint: 'تم إرسال الرد - الدرجة: {{score}}/{{max}}',
    responsesClosed: 'الردود مغلقة',
    submitResponse: 'إرسال الرد',
    yourResponse: 'ردك',
    saveChanges: 'حفظ التغييرات',
    respondingTo: 'الرد على {{title}}',
    responsePlaceholder: 'اكتب ردك الخاص. يمكن للإجابات الطويلة أن تتوسع أثناء الكتابة…',
    submitting: 'جارٍ الإرسال…',
    dueStatusOpen: 'مفتوح',
    dueStatusClosingSoon: 'يُغلق قريبًا',
    dueStatusClosed: 'مغلق',
    eventBannerLabel: 'حدث',
    eventAriaLabel: 'حدث: {{title}}',
    eventWhen: 'متى',
    eventStarts: 'يبدأ',
    eventEnds: 'ينتهي',
    eventOver: 'انتهى هذا الحدث',
    attendanceNoConfirmationsOwn: 'لم يؤكد أحد بعد',
    attendanceConfirm: 'تأكيد الحضور',
    attendanceOneConfirmedOwn: 'شخص واحد أكّد',
    attendanceYouConfirmed: 'لقد أكّدت',
    attendanceCountConfirmedOwn: '{{count}} أشخاص أكّدوا',
    attendanceYouAndOthers: 'أنت و{{count}} آخرين أكّدوا',
    attendanceModalAria: 'حضور الحدث',
    attendanceModalTitle: 'الحضور المؤكّد',
    attendanceLoading: 'جارٍ تحميل الحضور المؤكّد…',
    attendanceErrorTitle: 'تعذّر تحميل الحضور',
    attendanceErrorDescription: 'يرجى إغلاق هذه النافذة والمحاولة مرة أخرى.',
    attendanceEmptyTitle: 'لا تأكيدات بعد',
    attendanceEmptyDescription: 'عندما يؤكد الأشخاص، سيظهرون هنا.',
    googleMeetSectionAria: 'Google Meet',
    googleMeetReady: 'Google Meet جاهز',
    googleMeetCopyLink: 'نسخ رابط الاجتماع',
    googleMeetLinkCopied: 'تم نسخ الرابط',
    googleMeetJoin: 'انضم عبر Google Meet',
    googleMeetCopiedStatus: 'تم نسخ الرابط إلى الحافظة',
    googleSignInButton: 'تسجيل الدخول بـ Google',
    googleSigningIn: 'جارٍ تسجيل الدخول…',
    googleSignInPreviewSignedOut: 'معاينة حالة تسجيل الخروج',
    googleSignInPreviewSignedIn: 'معاينة حالة تسجيل الدخول',

    // Report post
    reportPost: 'إبلاغ',
    reportModalTitle: 'إبلاغ',
    reportModalAria: 'الإبلاغ عن منشور',
    reportOptionSpam: 'إنه بريد عشوائي',
    reportOptionInappropriate: 'إنه غير لائق',
    reportOptionHarmful: 'إنه مسيء أو ضار',
    reportOptionOther: 'سبب آخر',
    reportOtherPlaceholder: 'يرجى وصف المشكلة…',
    reportSubmit: 'إرسال',
    reportSubmittedTitle: 'شكراً لإبلاغنا',
    reportSubmittedBody: 'تم إرسال بلاغك وسنراجعه قريباً.',
    reportDone: 'تم',
    reportError: 'فشل إرسال البلاغ. يرجى المحاولة مرة أخرى.',

    likesTitle: 'إعجابات',
    likesTitle_one: 'إعجاب واحد',
    likesTitle_other: '{{count}} إعجاب',
    likesLoading: 'جارٍ تحميل الإعجابات…',
    likesErrorTitle: 'تعذّر تحميل الإعجابات',
    likesErrorDescription: 'يرجى المحاولة مرة أخرى بعد لحظة.',
    likesEmptyTitle: 'لا إعجابات بعد',
    likesEmptyDescription: 'عندما يعجب شخص بمنشورك، سيظهر هنا.',
    insightsTitle: 'إحصاءات المنشور',
    insightsSummaryAria: 'ملخص تحليلات المنشور',
    insightsTotalImpressions: 'إجمالي المشاهدات',
    insightsTotalImpressionsHint: 'كل مشاهدة تُحتسب',
    insightsViewers: 'المشاهدون',
    insightsViewersHint: 'كل مستخدم يُحتسب مرة واحدة',
    insightsLoadAnalyticsError: 'تعذّر تحميل التحليلات.',
    insightsEngagementHeading: 'التفاعل',
    insightsLikes: 'إعجابات',
    insightsComments: 'تعليقات',
    insightsClicks: 'نقرات',
    insightsMediaViews: 'مشاهدات الوسائط',
    insightsViewersByImpressionsHeading: 'المشاهدون حسب المشاهدات',
    insightsViewersByImpressionsHint: 'المشاهدات لكل مشاهد؛ الأعداد فوق 10 تظهر كـ 10+.',
    insightsViewersTotal: '{{count}} إجمالي',
    insightsNoViewersTitle: 'لا مشاهدين بعد',
    insightsNoViewersDescription: 'عندما يشاهد الناس هذا المنشور، سيظهرون هنا مع عدد مشاهداتهم.',
    insightsLoadViewersErrorTitle: 'تعذّر تحميل المشاهدين',
    insightsTryAgainDescription: 'يرجى المحاولة مرة أخرى بعد لحظة.',
    insightsViewerImpressionsAria: '{{count}} مشاهدة من هذا المشاهد',
    insightsLoadMoreViewers: 'تحميل المزيد من المشاهدين',
    insightsAnonymousUser: 'مستخدم MyU',
    responsePrivateLabel: 'رد خاص',
    responseRejectedBanner: 'تم رفض هذا الرد.',
    responseYourReview: 'مراجعتك',
    responseGradeLabel: 'الدرجة',
    responseGradeOutOf: '(من {{max}} نقطة)',
    responseGradePlaceholder: 'أدخل الدرجة',
    responseGradeInvalid: 'أدخل درجة صحيحة.',
    responseGradeBelowZero: 'لا يمكن أن تكون الدرجة أقل من 0.',
    responseGradeAboveMax: 'لا يمكن أن تتجاوز الدرجة {{max}} نقطة.',
    responseSaveGrade: 'حفظ الدرجة',
    responseGradeSaved: 'تم الحفظ · {{score}} / {{max}}',
    responseGradeDisplay: '{{score}} / {{max}} نقطة',
    responseYourGrade: 'درجتك',
    responseGradeReceivedNote: 'تلقيت درجة على هذه الإجابة.',
    responseRejectedLabel: 'تم رفض الإجابة',
    responseRejectedMessage:
      'تم رفض هذه الإجابة. يمكنك تقديم إجابة جديدة أو إجراء تعديلات وتقديمها مجددًا.',
    responseSubmitAgain: 'تقديم مجددًا',
    responseHideFeedback: 'إخفاء الملاحظات',
    responseViewFeedback: 'عرض الملاحظات ({{count}})',
    responseGiveFeedback: 'إضافة ملاحظات',
    responseReject: 'رفض',
    responseNoFeedbackYet: 'لا ملاحظات بعد. أضف ملاحظة لهذا المستلم أدناه.',
    responseFeedbackPlaceholder: 'اكتب ملاحظات لهذا المستلم…',
    responsePostFeedback: 'نشر الملاحظات',
    responseFeedbackFromOwner: 'ملاحظات من صاحب السؤال',
    responseTeacherFallback: 'معلّم',
    loadingResponse: 'جارٍ تحميل إجابتك…',
    loadingFeedback: 'جارٍ تحميل الملاحظات…',
    noResponseContent: 'لم يتم إرسال أي محتوى نصي.',
    noFeedbackYet: 'لا توجد ملاحظات من صاحب السؤال بعد.',
    questionOwner: 'صاحب السؤال',
    editResponse: ' تعديل الإجابة',
    commentReplyingTo: 'الرد على @{{username}}',
    commentCancelReply: 'إلغاء الرد',
    commentPlaceholder: 'اكتب تعليقًا…',
    commentReply: 'رد',
    commentViewMoreReplies_one: 'عرض رد إضافي واحد',
    commentViewMoreReplies_other: 'عرض {{count}} ردود إضافية',
    commentHideReplies: 'إخفاء الردود',
    commentOptionsAria: 'خيارات التعليق',
    commentDelete: 'حذف',
    commentDeleting: 'جارٍ الحذف…',
    commentViewImageAria: 'عرض {{name}}',
    playVideo: 'تشغيل الفيديو',
    videoThumbnailAlt: 'صورة مصغّرة للفيديو',
    galleryOpenImage: 'فتح الصورة',
    galleryViewAllImagesMore: 'عرض جميع الصور، {{count}} إضافية',
    galleryMoreOverlay: '+{{count}} المزيد',
    galleryAriaLabel: 'معرض الصور',
    galleryImageAlt: 'صورة {{index}} من {{total}}',
    galleryPrevious: 'الصورة السابقة',
    galleryNext: 'الصورة التالية',
    galleryDownload: 'تنزيل',
    galleryClose: 'إغلاق المعرض',
    galleryViewImage: 'عرض الصورة {{index}}',
    documentPreviewAria: 'معاينة {{name}}',
    documentPreviewIframeTitle: 'معاينة {{name}}',
    documentCantSeePreview: 'لا ترى المعاينة؟',
    documentDownloadTheFile: 'تنزيل الملف',
    documentNoBrowserPreview: 'المعاينة غير متاحة في المتصفح. نزّل الملف لفتحه.',
    documentNoPreviewUrl: 'لا يوجد رابط معاينة متاح لهذا الملف.',
    documentDownloadFile: 'تنزيل الملف',
    documentTypePdf: 'مستند PDF',
    documentTypeWord: 'مستند Word',
    documentTypePpt: 'عرض PowerPoint',
    documentTypeExcel: 'جدول Excel',
    videoPreviewAria: 'معاينة الفيديو',
    videoTitle: 'فيديو',
  },
  messages: {
    title: 'الرسائل',
    historyError: 'تعذّر تحميل سجل المحادثات: {{error}}',
    fetchingNew: 'جارٍ جلب رسائل جديدة…',
    fetchingInitial: 'جارٍ جلب رسائلك…',
    unreadMessages: 'رسائل غير مقروءة',
    searchConversations: 'البحث في المحادثات…',
    searchArchived: 'البحث في المؤرشف…',
    noArchivedChats: 'لا توجد محادثات مؤرشفة',
    noConversationsFound: 'لم يتم العثور على محادثات',
    archiveHint: 'أرشِف محادثة من رأسها أو من قائمة المحادثة لتجدها هنا.',
    searchHint: 'جرّب بحثًا مختلفًا أو ابدأ محادثة جديدة.',
    chats: 'المحادثات',
    archived: 'المؤرشف',
    emptyTitle: 'رسائلك',
    emptyDescription: 'اختر محادثة أو ابدأ محادثة جديدة مع شخص تتابعه أو تشاركه صفًا.',
    newConversation: 'محادثة جديدة',
    newChat: '+ جديد',
    startConversationAria: 'بدء محادثة جديدة',
    typing: 'يكتب…',
    member_one: 'عضو واحد',
    member_other: '{{count}} أعضاء',
    group: 'مجموعة',
    online: 'متصل',
    offline: 'غير متصل',
    backToConversations: 'العودة إلى المحادثات',
    searchInConversation: 'البحث في المحادثة',
    pinConversation: 'تثبيت المحادثة',
    unpinConversation: 'إلغاء تثبيت المحادثة',
    archiveConversation: 'أرشفة المحادثة',
    unarchiveConversation: 'إلغاء أرشفة المحادثة',
    viewContactInfo: 'عرض معلومات جهة الاتصال',
    viewGroupInfo: 'عرض معلومات المجموعة',
    pinned: 'مثبّت',
    pin: 'تثبيت',
    unpin: 'إلغاء التثبيت',
    moveToChats: 'نقل إلى المحادثات',
    archive: 'أرشفة',
    conversationOptions: 'خيارات المحادثة',
    typeMessage: 'اكتب رسالة…',
    composerKeyboardHint:
      'Enter للإرسال. Shift+Enter أو Option+Enter لسطر جديد. Escape لإلغاء الرد.',
    unblock: 'إلغاء الحظر',
    recording: 'جارٍ التسجيل…',
    attachFile: 'إرفاق ملف',
    attachImage: 'إرفاق صورة',
    addEmoji: 'إضافة رمز تعبيري',
    sendMessage: 'إرسال رسالة',
    recordVoice: 'تسجيل رسالة صوتية',
    cancelRecording: 'إلغاء التسجيل الصوتي',
    sendVoice: 'إرسال رسالة صوتية',
    micNotSupported: 'متصفحك لا يدعم الوصول إلى الميكروفون.',
    voiceNotSupported: 'التسجيل الصوتي غير مدعوم في هذا المتصفح.',
    micAccessFailed: 'تعذّر الوصول إلى الميكروفون. يرجى التحقق من الأذونات والمحاولة مرة أخرى.',
    newGroup: 'مجموعة جديدة',
    newGroupDescription: 'راسل عدة أشخاص في وقت واحد',
    groupChat: 'محادثة جماعية',
    peopleYouKnow: 'أشخاص تعرفهم',
    loadingContacts: 'جارٍ تحميل جهات الاتصال…',
    contactsLoadError: 'تعذّر تحميل جهات الاتصال',
    tryAgain: 'حاول مرة أخرى',
    noMatches: 'لا توجد نتائج',
    noMatchesHint: 'جرّب اسمًا أو صفًا مختلفًا.',
    searchContacts: 'البحث في جهات الاتصال',
    searchByNameOrClass: 'البحث بالاسم أو الصف…',
    chooseSomeone: 'اختر شخصًا لمراسلته',
    chooseMembers: 'اختر الأعضاء',
    groupDetails: 'تفاصيل المجموعة',
    stepOneOfTwo: 'الخطوة 1 من 2',
    stepTwoOfTwo: 'الخطوة 2 من 2',
    createGroup: 'إنشاء مجموعة',
    goBack: 'رجوع',
    searchMembers: 'البحث في الأعضاء',
    selectedCount: '{{selected}} محدد / {{limit}}',
    contactsCount: '({{count}} جهة اتصال)',
    selectTwoPeople: 'اختر شخصين على الأقل لإنشاء مجموعة',
    maxParticipants: 'الحد الأقصى {{limit}} مشارك لكل مجموعة',
    addGroupPhoto: 'إضافة صورة للمجموعة',
    changeGroupPhoto: 'تغيير صورة المجموعة',
    groupAvatarPreview: 'معاينة صورة المجموعة',
    addPhoto: 'إضافة صورة',
    removePhoto: 'إزالة الصورة',
    groupName: 'اسم المجموعة',
    groupNamePlaceholder: 'مثال: مجموعة دراسة CS 101',
    participants: 'المشاركون ({{count}})',
    groupInfo: 'معلومات المجموعة',
    info: 'معلومات',
    viewProfile: 'عرض الملف الشخصي',
    closeContactInfo: 'إغلاق معلومات جهة الاتصال',
    membersLabel: 'الأعضاء · {{count}}',
    admin: 'مسؤول',
    removeMember: 'إزالة {{name}}',
    removeMemberConfirm: 'إزالة {{name}} من {{group}}؟',
    sharedMedia: 'الوسائط المشتركة',
    noSharedMedia: 'لا توجد وسائط مشتركة بعد',
    deleteChatConfirm: 'حذف هذه المحادثة؟',
    exitGroupOwnerConfirm:
      'مغادرة "{{name}}"؟\n\nبما أنك مالك هذه المجموعة، فبمغادرتها لن يتمكن الأعضاء الآخرون من الدردشة.',
    exitGroupConfirm: 'مغادرة "{{name}}"؟',
    exitGroup: 'مغادرة المجموعة',
    deleteChat: 'حذف المحادثة',
    notGroupMember: 'أنت لست عضوًا في هذه المجموعة',
    ownSettingsBlock: 'إعداداتك الحالية لا تسمح بمراسلة هذا المستخدم. تعديل الإعدادات',
    promoterNoMessages: 'هذا الحساب لا يستقبل رسائل',
    institutionDisabled: 'تم تعطيل هذه الميزة من قبل مؤسستك',
    userNoMessages: 'هذا المستخدم لا يريد استقبال رسائل خاصة في الوقت الحالي',
    unknownUserBanner: 'لم تتحدث مع هذا المستخدم من قبل',
    block: 'حظر',
    accept: 'قبول',
    undo: 'تراجع',
    dismiss: 'تجاهل',
    selectMessages: 'اختر الرسائل',
    tapToAddRemove: '— انقر للإضافة أو الإزالة',
    forward: 'إعادة توجيه',
    forwardWithCount: 'إعادة توجيه ({{count}})',
    searchInConversationPlaceholder: 'البحث في المحادثة…',
    searchMessageText: 'البحث في نص الرسالة…',
    filterText: 'نص',
    filterMedia: 'صور وفيديو',
    filterFiles: 'ملفات',
    filterLinks: 'روابط',
    filterAll: 'الكل',
    match_one: 'نتيجة واحدة',
    match_other: '{{count}} نتائج',
    messageOptionsAria: 'خيارات الرسالة',
    reply: 'رد',
    download: 'تنزيل',
    shareTo: 'مشاركة إلى…',
    shareAction: 'مشاركة',
    sharePhotosCount: '{{count}} صور',
    sharePhoto: 'صورة',
    shareVideo: 'فيديو',
    shareVoiceMessage: 'رسالة صوتية ({{duration}})',
    shareMessageFallback: 'رسالة',
    shareForwardOne: 'إعادة توجيه رسالة',
    shareForwardMany: 'إعادة توجيه {{count}} رسائل',
    shareShareOne: 'مشاركة رسالة',
    shareShareMany: 'مشاركة {{count}} رسائل',
    shareForwarded: 'تمت إعادة التوجيه',
    shareSharedMany: 'تمت مشاركة {{count}} رسائل',
    shareSharedOne: 'تمت مشاركة الرسالة',
    shareSharedTo: 'إلى {{name}}. افتح تلك المحادثة لعرض {{pronoun}}.',
    shareChooseConversation: 'اختر محادثة',
    statusSending: 'جارٍ الإرسال',
    statusSent: 'تم الإرسال',
    statusDelivered: 'تم التسليم',
    statusRead: 'تمت القراءة',
    mediaUploading: 'جارٍ الرفع…',
    statusFailed: 'فشل الإرسال',
    emojiCategorySmileys: 'وجوه',
    emojiCategoryGesturesPeople: 'إيماءات وأشخاص',
    emojiCategoryHeartsSymbols: 'قلوب ورموز',
    emojiCategoryAnimalsNature: 'حيوانات وطبيعة',
    emojiCategoryFoodDrink: 'طعام وشراب',
    emojiCategoryTravelObjects: 'سفر وأشياء',
    pickEmojiAria: 'اختر رمزًا تعبيريًا',
    insertEmojiAria: 'إدراج {{emoji}}',
    sharePronounIt: 'ذلك',
    sharePronounThem: 'تلك',
    groupReadByOne: 'قرأها {{name}}',
    groupReadByCount: 'قرأها {{count}}',
    groupCloseReadListAria: 'إغلاق قائمة القراءة',
    mediaViewerAria: 'عارض الوسائط',
    mediaCounter: '{{current}} / {{total}}',
    preview: 'معاينة',
    closeViewerAria: 'إغلاق العارض',
    systemMemberAdded: '{{actor}} أضاف {{target}}',
    systemMemberRemoved: '{{actor}} أزال {{target}}',
    systemYouLeft: 'غادرت',
    systemMemberLeft: '{{actor}} غادر',
    systemAdminPromoted: '{{actor}} جعل {{target}} مشرفًا',
    systemGroupCreatedNamed: '{{actor}} أنشأ "{{groupName}}"',
    systemGroupCreated: '{{actor}} أنشأ المجموعة',
    systemDefaultActor: 'أنت',
    replyingTo: 'الرد على {{name}}',
    cancelReplyAria: 'إلغاء الرد',
    quickReactionsAria: 'تفاعلات سريعة',
    addReactionAria: 'إضافة تفاعل',
    retry: 'إعادة المحاولة',
    playVoiceMessage: 'تشغيل الرسالة الصوتية',
    pauseVoiceMessage: 'إيقاف الرسالة الصوتية',
    playing: 'جارٍ التشغيل…',
    noSearchMatches: 'لا توجد رسائل مطابقة لبحثك.',
  },
  calendar: {
    loadingTimetable: 'جارٍ تحميل الجدول…',
    noEvents: 'لا توجد أحداث مجدولة لهذا اليوم',
    selectDate: 'اختر تاريخ التقويم',
    addEvent: 'إضافة حدث للتقويم',
    emptyTitle: 'لا توجد أحداث للصف',
    emptyDescription: 'الأحداث المجدولة وتواريخ الاستحقاق لهذا الصف ستظهر هنا.',
  },
  feed: {
    everyone: 'الجميع',
    filterByChild: 'تصفية الخلاصة حسب الطفل',
    loadingFeed: 'جارٍ تحميل الخلاصة…',
    loadError: 'تعذّر تحميل الخلاصة. اسحب للأسفل للمحاولة مرة أخرى.',
    loadClassFeedError: 'تعذّر تحميل خلاصة الصف.',
    loadingMore: 'جارٍ تحميل المزيد من المنشورات…',
    noMorePosts: 'لا مزيد من المنشورات',
    feedBreadcrumb: 'الخلاصة',
    hashtagBreadcrumb: 'وسم',
    hashtagDescription: 'المنشورات العامة ومنشورات الصف التي يمكنك الوصول إليها وتتضمن هذا الوسم.',
    noPostsTitle: 'لا توجد منشورات بعد',
    noPostsDescription: 'لا توجد منشورات تحتوي على #{{tag}} في النصوص التي يمكنك عرضها.',
  },
  explore: {
    trendingPosts: 'المنشورات الرائجة',
    noTrendingTitle: 'لا توجد منشورات رائجة بعد',
    noTrendingDescription: 'ستظهر منشورات مدرستك وشبكتك هنا عندما يكون هناك نشاط لاستكشافه.',
    peopleYouMayKnow: 'أشخاص قد تعرفهم',
    noSuggestionsTitle: 'لا توجد اقتراحات حاليًا',
    noSuggestionsDescription: 'سيظهر أشخاص من مدرستك هنا عندما تكون هناك حسابات لاكتشافها.',
    follow: 'متابعة',
    requested: 'مطلوب',
    following: 'متابَع',
    followAria: 'متابعة {{name}}',
    unfollowAria: 'إلغاء متابعة {{name}}',
    mutualConnection_one: 'اتصال مشترك واحد',
    mutualConnection_other: '{{count}} اتصالات مشتركة',
  },
  createPost: {
    post: 'نشر',
    postEvent: 'نشر الحدث',
    postQuestion: 'نشر السؤال',
    posting: 'جارٍ النشر…',
    captionPlaceholder: 'ما الذي يدور في ذهنك؟ استخدم #وسوم أو @إشارات',
    captionPlaceholderShort: 'ما الذي يدور في ذهنك؟',
    postCaptionAria: 'نص المنشور',
    eventCollapsedHint: 'أنشئ حدثًا بالتاريخ والوقت وGoogle Meet اختياريًا…',
    questionCollapsedHint: 'اطرح سؤالًا بردود خاصة…',
    composerTypeAria: 'نوع المحرر',
    modePost: 'منشور',
    modeQuestion: 'سؤال',
    modeEvent: 'حدث',
    sentToTitle: 'إرسال إلى',
    preparingPost: 'جارٍ تجهيز المنشور…',
    uploadingMedia: 'جارٍ رفع الوسائط…',
    processingMedia: 'جارٍ معالجة الوسائط…',
    publishingPost: 'جارٍ نشر المنشور…',
    imageSelectionCount: 'تم تحديد {{count}} من {{limit}} صور.',
    imageLimitReached: 'تم الوصول إلى حد الصور: تم تحديد {{count}} من {{limit}}.',
    storageFullAttachmentNotice:
      'مساحة التخزين لديك ممتلئة. لا يزال بإمكانك نشر الصور والصوت، لكن مقاطع الفيديو والمستندات غير متاحة.',
    publicFeed: 'الخلاصة العامة',
    public: 'عام',
    anyoneFollowing: 'أي شخص يتابعك',
    follower_one: 'متابع واحد',
    follower_other: '{{count}} متابع',
    publicExpandedHint: 'مرئي للمستخدمين الذين يتابعونك في الخلاصة العامة.',
    postToPublicAria: 'النشر في الخلاصة العامة',
    yourClasses: 'صفوفك',
    gradedClassHint:
      'اختر صفًا واحدًا على الأقل واختر فئة التقييم لكل صف. فقط المستلمون في قائمة الأسماء يمكنهم إرسال ردود خاصة.',
    classLabel: 'صف',
    sendToAllMembersAria: 'إرسال إلى جميع أعضاء {{className}}',
    selectClassAria: 'اختيار {{className}}',
    allMembersOfAria: 'جميع أعضاء {{className}}',
    sendToMemberAria: 'إرسال إلى {{name}}',
    recipientsSelected_one: 'تم اختيار مستلم واحد.',
    recipientsSelected_other: 'تم اختيار {{count}} مستلم.',
    privateResponseHint: 'الردود مرئية لك وللمُجيب فقط — وليس في الخلاصة العامة.',
    questionTitle: 'عنوان السؤال',
    questionTitlePlaceholder: 'مثال: تأمل الفصل الخامس',
    instructions: 'التعليمات',
    instructionsHint: 'صف ما يجب على المستلمين تقديمه. يمكنك إرفاق ملفات أدناه.',
    instructionsPlaceholder: 'ماذا يجب أن يتضمن رد المستلمين؟',
    dueDate: 'تاريخ الاستحقاق',
    dueTime: 'وقت الاستحقاق',
    gradeResponses: 'تقييم الردود',
    gradedQuestionHint:
      'يمكن مشاركة الأسئلة المُقيّمة مع الصفوف فقط. فقط المستلمون في قائمة أسماء الصف يمكنهم إرسال ردود خاصة.',
    gradingTerm: 'فترة التقييم',
    selectTerm: 'اختر الفترة',
    noGradingTerms: 'لم يتم إعداد فترات تقييم لصفوفك',
    gradingTermsLoadError: 'تعذّر تحميل فترات التقييم',
    gradingCategoryNextStepHint: 'ستختار فئة التقييم وقيمة النقاط لكل صف في الشاشة التالية.',
    allowEditBeforeDue: 'السماح للمستلمين بتعديل الردود قبل تاريخ الاستحقاق',
    chooseRecipients: 'اختر المستلمين',
    eventName: 'اسم الحدث',
    eventNamePlaceholder: 'اسم الحدث',
    allDay: 'طوال اليوم',
    allDayDescription: 'إخفاء أوقات البداية والنهاية عند التفعيل.',
    starts: 'يبدأ',
    startDate: 'تاريخ البداية',
    ends: 'ينتهي',
    endDate: 'تاريخ النهاية',
    time: 'الوقت',
    googleSignInTitle: 'سجّل الدخول لاستخدام ميزات Google',
    googleSignInDescription:
      'بصفتك مضيف الحدث، سجّل الدخول باستخدام Google لإرفاق رابط Meet أو حفظ هذا الحدث في Google Calendar عند النشر.',
    addGoogleMeet: 'إضافة Google Meet',
    meetEnabledDescription: 'سيتم إنشاء رابط Meet عند النشر.',
    meetDisabledDescription: 'يتطلب تسجيل الدخول بـ Google. يمكن للحضور الانضمام دون تسجيل الدخول.',
    addToCalendar: 'إضافة إلى تقويمك',
    addToCalendarDescription: 'احفظ هذا الحدث في تقويمك عند النشر.',
    phoneCalendar: 'تقويم الهاتف',
    phoneCalendarDescription: 'قدّم أيضًا تنزيل تقويم الهاتف للمستلمين.',
    googleCalendar: 'Google Calendar',
    googleCalendarDescription:
      'يتطلب تسجيل الدخول بـ Google. يُضاف إلى تقويمك فقط، وليس إلى المستلمين.',
    previewSignedOut: 'معاينة حالة تسجيل الخروج',
    previewSignedIn: 'معاينة حالة تسجيل الدخول',
    description: 'الوصف',
    descriptionHint: 'اختياري. سيرى الحضور هذا في منشور الحدث.',
    descriptionPlaceholder: 'أضف وصفًا',
    uploadPhotos: 'رفع صور',
    takePhoto: 'التقاط صورة',
    uploadVideo: 'رفع فيديو',
    recordVideo: 'تسجيل فيديو',
    uploadAudio: 'رفع صوت',
    recordVoiceNote: 'تسجيل ملاحظة صوتية',
    uploadDocument: 'رفع مستند',
    photos: 'صور',
    video: 'فيديو',
    addDocument: 'إضافة مستند',
    voice: 'صوت',
    mentionSomeone: 'الإشارة إلى شخص',
    noMatches: 'لا توجد نتائج',
    noMatchesHint: 'جرّب اسمًا أو معرّفًا مختلفًا.',
    gradingForClass: 'التقييم لهذا الصف',
    category: 'الفئة',
    selectCategory: 'اختر الفئة',
    totalGrade: 'الدرجة الكلية',
    previewAttachmentAria: 'معاينة {{name}}',
    removeAttachmentAria: 'إزالة {{name}}',
    takePhotoTitle: 'التقاط صورة',
    startingCamera: 'جارٍ تشغيل الكاميرا…',
    cameraPreview: 'معاينة الكاميرا',
    capturing: 'جارٍ الالتقاط…',
    capturePhoto: 'التقاط صورة',
    recordVideoTitle: 'تسجيل فيديو',
    recordedVideoPreview: 'معاينة الفيديو المسجّل',
    recLabel: 'تسجيل {{duration}}',
    stopRecording: 'إيقاف التسجيل',
    startRecording: 'بدء التسجيل',
    retake: 'إعادة المحاولة',
    addVideo: 'إضافة فيديو',
    videoNotSupported: 'تسجيل الفيديو غير مدعوم في هذا المتصفح.',
    recordVoiceTitle: 'تسجيل ملاحظة صوتية',
    startingMicrophone: 'جارٍ تشغيل الميكروفون…',
    voicePreview: 'معاينة الصوت',
    recordAgain: 'تسجيل مرة أخرى',
    addVoiceNote: 'إضافة ملاحظة صوتية',
    voiceNotSupported: 'التسجيل الصوتي غير مدعوم في هذا المتصفح.',
    mediaNotSupported: 'الكاميرا والميكروفون غير مدعومين في هذا المتصفح.',
    mediaAccessFailed: 'تعذّر الوصول إلى الكاميرا أو الميكروفون. تحقق من الأذونات وحاول مرة أخرى.',
    validationQuestionTitleRequired: 'أضف عنوانًا للسؤال.',
    validationDueDateRequired: 'اختر تاريخ الاستحقاق.',
    validationDueTimeRequired: 'اختر وقت الاستحقاق.',
    validationGradingTermRequired: 'اختر فترة التقييم.',
    validationSelectGradingCategory: 'اختر فئة التقييم لهذا الصف.',
    validationTotalGradeInvalid: 'أدخل درجة كلية صالحة لهذا الصف.',
    validationEventNameRequired: 'اسم الحدث مطلوب.',
    validationStartDateRequired: 'تاريخ البداية مطلوب.',
    validationEndDateRequired: 'تاريخ النهاية مطلوب.',
    validationTimesRequired: 'أوقات البداية والنهاية مطلوبة.',
    validationEndAfterStart: 'يجب أن يكون الانتهاء بعد البداية.',
    validationGoogleSignInRequired:
      'سجّل الدخول بـ Google لإضافة Google Meet أو الحفظ في Google Calendar.',
    errorLoadingClasses: 'جارٍ تحميل صفوفك — يرجى المحاولة مرة أخرى بعد لحظة.',
    errorSelectAudience: 'اختر جمهورًا واحدًا على الأقل.',
    errorSelectClass: 'اختر صفًا واحدًا على الأقل.',
    errorInvalidDueDate: 'تاريخ استحقاق غير صالح.',
    errorInvalidEventDateTime: 'تاريخ/وقت حدث غير صالح.',
    errorPublishQuestionFailed: 'فشل نشر السؤال.',
    errorPublishPostFailed: 'فشل نشر المنشور.',
    errorPublishEventFailed: 'فشل نشر الحدث.',
  },
  profile: {
    profileNotFoundTitle: 'الملف الشخصي غير موجود',
    profileNotFoundDescription: 'هذا المستخدم غير موجود أو ملفه الشخصي غير متاح.',
    profileUnavailableTitle: 'الملف الشخصي غير متاح',
    profileUnavailableDescription:
      'لقد حظرت هذا الحساب. ألغِ الحظر من الإعدادات لعرض ملفه الشخصي مرة أخرى.',
    loadingProfileAria: 'جارٍ تحميل الملف الشخصي',
    loadingPosts: 'جارٍ تحميل المنشورات…',
    postsLoadError: 'تعذّر تحميل منشورات هذا الملف الشخصي.',
    noPostsYet: 'لا توجد منشورات بعد',
    noPosts: 'لا توجد منشورات',
    noPostsOwnDescription: 'شارك تحديثاً مع صفوفك أو الخلاصة العامة.',
    noPostsOtherDescription: 'لم ينشر هذا المستخدم أي شيء بعد.',
    writePost: 'اكتب منشوراً',
    privateAccount: 'حساب خاص',
    privateAccountFollowingDescription:
      'هذا الحساب خاص. ستظهر قوائم المنشورات والاتصالات هنا عندما تكون متاحة للمتابعين.',
    privateAccountFollowDescription:
      'تظهر الأعداد أعلاه، لكن تابع هذا الحساب لعرض منشوراته ومتابعيه وصفوفه المنضم إليها.',
    follow: 'متابعة',
    classOf: 'دفعة {{year}}',
    classesSectionTitle: 'صفوف {{name}}',
    shareProfile: 'مشاركة الملف الشخصي',
    shareProfileAria: 'مشاركة الملف الشخصي',
    qrCodeAria: 'رمز QR لرابط الملف الشخصي',
    linkCopied: 'تم نسخ الرابط',
    copyProfileLink: 'نسخ رابط الملف الشخصي',
    flyerDownloaded: 'تم تنزيل المنشور',
    downloadProfileFlyer: 'تنزيل منشور الملف الشخصي',
    leaveClass: 'مغادرة الصف',
    leaveClassAria: 'مغادرة الصف',
    leaveClassConfirm:
      'مغادرة {{className}}؟ ستفقد الوصول إلى محتوى الصف والمنشورات والأعضاء حتى تنضم مرة أخرى.',
    editProfile: 'تعديل الملف الشخصي',
    profileDetails: 'تفاصيل الملف الشخصي',
    profileDetailsDescription:
      'حدّث كيف يراك الآخرون على MyU. قد تكون بعض الحقول مقفلة من قبل مدرستك.',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    username: 'اسم المستخدم',
    usernameHint: 'حروف وأرقام وشرطات سفلية فقط.',
    institution: 'المؤسسة',
    selectInstitution: 'اختر المؤسسة',
    searchInstitutions: 'البحث في المؤسسات…',
    institutionOptions: 'خيارات المؤسسة',
    bio: 'نبذة',
    bioRemaining: '{{count}} حرف متبقٍ',
    bioPlaceholder: 'أخبر الآخرين قليلاً عن نفسك',
    personalInfo: 'المعلومات الشخصية',
    personalInfoDescription: 'تفاصيل خاصة تُستخدم لاسترداد الحساب وسجلات المدرسة.',
    email: 'البريد الإلكتروني',
    mobileNumber: 'رقم الجوال',
    mobilePlaceholder: '+1 555 000 0000',
    gender: 'الجنس',
    dateOfBirth: 'تاريخ الميلاد',
    changePassword: 'تغيير كلمة المرور',
    lockedFieldHint: 'يُدار هذا الحقل من قبل مسؤول مدرستك.',
    genderFemale: 'أنثى',
    genderMale: 'ذكر',
    genderNonBinary: 'غير ثنائي',
    genderPreferNotToSay: 'أفضل عدم الإفصاح',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    newPasswordPlaceholder: '6 أحرف على الأقل (حروف، أرقام، _ # @)',
    confirmNewPassword: 'تأكيد كلمة المرور الجديدة',
    passwordTooShort: 'يجب أن تكون كلمة المرور 6 أحرف على الأقل.',
    passwordInvalidChars: 'يُسمح فقط بالحروف والأرقام والرموز _ # @.',
    passwordsMismatch: 'كلمتا المرور غير متطابقتين.',
    passwordUpdated: 'تم تحديث كلمة مرورك.',
    updatingPassword: 'جارٍ التحديث…',
    updatePassword: 'تحديث كلمة المرور',
    enterCurrentPassword: 'أدخل كلمة مرورك الحالية.',
    newPasswordMinLength: 'يجب أن تكون كلمة المرور الجديدة 6 أحرف على الأقل.',
    newPasswordInvalidChars: 'يمكن أن تحتوي كلمة المرور الجديدة فقط على حروف وأرقام و _ # @.',
    newPasswordMustDiffer: 'يجب أن تختلف كلمة المرور الجديدة عن كلمة مرورك الحالية.',
    mustBeSignedIn: 'يجب تسجيل الدخول لتغيير كلمة المرور.',
    passwordUpdateFailed: 'فشل تحديث كلمة المرور.',
    manageCategories: 'إدارة الفئات',
    manageCategoriesAria: 'إدارة الفئات',
    sortingCategoryOrder: 'ترتيب ترتيب الفئات',
    sortingUncategorized: 'ترتيب الصفوف غير المصنّفة',
    sortingClassesIn: 'ترتيب الصفوف في {{name}}',
    doneSorting: 'تم الترتيب',
    sortCategoryOrderHint: 'استخدم أعلى وأسفل لتغيير ترتيب ظهور الفئات في ملفك الشخصي.',
    uncategorizedTopHint: 'تظهر هذه الصفوف في أعلى ملفك الشخصي عندما لا تكون في أي فئة.',
    noUncategorizedClasses: 'لا توجد صفوف غير مصنّفة.',
    sortClassesInCategoryHint: 'استخدم أعلى وأسفل لتغيير ترتيب الصفوف داخل هذه الفئة.',
    noClassesInCategory: 'لا توجد صفوف في هذه الفئة بعد.',
    loadingCategories: 'جارٍ تحميل الفئات…',
    categoriesLoadError: 'تعذّر تحميل فئاتك. يُعرض آخر قائمة معروفة.',
    uncategorizedClasses: 'صفوف غير مصنّفة',
    atTopOfProfile: '{{count}} في أعلى الملف الشخصي',
    sortOrder: 'ترتيب الفرز',
    allClassesInCategory: 'جميع الصفوف في فئة.',
    categoryName: 'اسم الفئة',
    class_one: 'صف واحد',
    class_other: '{{count}} صف',
    sortClasses: 'ترتيب الصفوف',
    archive: 'أرشفة',
    delete: 'حذف',
    remove: 'إزالة',
    tickClassesHint: 'حدّد الصفوف لتضمينها. يمكن أن ينتمي الصف إلى فئة واحدة فقط.',
    alreadyInAnotherCategory: 'موجود بالفعل في فئة أخرى',
    newCategory: 'فئة جديدة',
    newCategoryPlaceholder: 'مثال: صفوف STEM',
    addCategory: 'إضافة فئة',
    addingCategory: 'جارٍ الإضافة…',
    saveChanges: 'حفظ التغييرات',
    untitledCategory: 'فئة بدون عنوان',
    expandCategory: 'توسيع الفئة',
    collapseCategory: 'طي الفئة',
    up: 'أعلى',
    down: 'أسفل',
    sortCategoryOrder: 'ترتيب ترتيب الفئات',
    categoryNamesUnique: 'يجب أن تكون أسماء الفئات فريدة.',
    couldNotAddCategory: 'تعذّرت إضافة الفئة. يرجى المحاولة مرة أخرى.',
    couldNotSaveChanges: 'تعذّر حفظ التغييرات. يرجى المحاولة مرة أخرى.',
    yourClassesAria: 'صفوفك',
    yourClasses: 'صفوفك ({{count}})',
    organiseCategories: 'تنظيم الفئات والترتيب',
    noClassesToShow: 'لا توجد صفوف للعرض.',
    addClass: 'إضافة صف',
    editClass: 'تعديل الصف',
    shareClass: 'مشاركة الصف',
    privateClass: 'صف خاص',
    changeProfilePhoto: 'تغيير صورة الملف الشخصي',
    actionsFor: 'إجراءات {{name}}',
    posts: 'منشورات',
    members: 'أعضاء',
    joined: 'منضم',
    requested: 'مطلوب',
    join: 'انضم',
    following: 'متابَع',
    message: 'رسالة',
    block: 'حظر',
    moreActionsFor: 'المزيد من الإجراءات لـ {{name}}',
    accountTypeStudent: 'طالب',
    accountTypeInstructor: 'معلّم',
    accountTypeManagement: 'كادر',
    accountTypePromoter: 'مروّج',
    accountTypeParent: 'ولي أمر',
    accountTypeTutor: 'مُدرّس خصوصي',
  },
  notifications: {
    title: 'الإشعارات',
    description: 'راجع طلبات الصف والدعوات أولاً، ثم اطلع على النشاط الأخير.',
    needsAttention: 'يحتاج انتباهك',
    requestedToFollow: 'طلب متابعتك',
    requestedToJoinClass: 'طلب الانضمام إلى صفك',
    classJoinRequests: 'طلبات الانضمام للصف',
    classJoinRequestsDescription: 'وافق أو ارفض الأعضاء الذين يريدون الانضمام إلى صفوفك.',
    classFollowRequests: 'طلبات المتابعة',
    classFollowRequestsDescription: 'وافق أو ارفض الأشخاص الذين يريدون متابعة ملفك الشخصي.',
    classAdminInvites: 'دعوات إدارة الصف',
    classAdminInvitesDescription: 'اقبل أو ارفض الدعوات للمساعدة في إدارة صف.',
    recentActivity: 'النشاط الأخير',
    tabFollowing: 'المتابَعون',
    tabYou: 'أنت',
    yourNotificationsAria: 'إشعاراتك',
    followingActivityAria: 'نشاط المتابَعين',
    loadError: 'تعذّر تحميل الإشعارات',
    emptyTitle: 'لا توجد إشعارات بعد',
    emptyYouDescription: 'سيظهر النشاط المتعلق بمنشوراتك وصفوفك هنا.',
    emptyFollowingDescription: 'ستظهر تحديثات الأشخاص الذين تتابعهم هنا.',
    requestedToJoin: 'طلب الانضمام إلى',
    adminInviteFor: 'دعوة إدارة لـ',
    approve: 'موافقة',
    decline: 'رفض',
    accept: 'قبول',
    pending_one: '{{count}} معلّق',
    pending_other: '{{count}} معلّق',
    andOther_one: ' و1 آخر',
    andOther_other: ' و{{count}} آخرين',
    pendingRequest_one: '{{count}} طلب معلّق',
    pendingRequest_other: '{{count}} طلبات معلّقة',
    notificationsAria: 'الإشعارات',
    notificationsPendingAria: 'الإشعارات، {{count}} معلّقة',
    likedYourPost: 'أعجب بمنشورك',
    commentedOnYourPost: 'علّق على منشورك',
    mentionedYouInComment: 'أشار إليك في تعليق',
    startedFollowingYou: 'بدأ متابعتك',
    joinedYourClass: 'انضم إلى صفك:',
    createdNewClass: 'أنشأ صفاً جديداً:',
    nowAdminOfClass: 'أصبح الآن مشرفاً على صفك:',
    invitedYouAsAdmin: 'دعاك لتكون مشرفاً على',
  },
  prime: {
    title: 'MyU Prime',
    goBack: 'رجوع',
    activePlan: 'الخطة النشطة',
    defaultPlanName: 'MyU Prime · سنة واحدة',
    activeUntil: 'وصولك المميز نشط حتى {{date}}.',
    noAutoRenewNote: 'لا يوجد تجديد تلقائي. يمكنك شراء سنة أخرى من هذه الصفحة عند انتهاء خطتك.',
    manageInSettings: 'إدارة في الإعدادات',
    annualPlan: 'الخطة السنوية',
    loadingPlan: 'جارٍ تحميل الخطة السنوية…',
    planUnavailable: 'الباقة السنوية غير متاحة حالياً. يرجى المحاولة لاحقاً.',
    oneTimePayment: 'دفعة واحدة · {{gb}} GB تخزين · جميع الميزات المميزة',
    subscribe: 'اشترك · {{label}}',
    redirectingCheckout: 'جارٍ تفعيل Prime…',
    checkoutError: 'تعذّر تفعيل Prime الآن. يرجى المحاولة مرة أخرى.',
    agreePrefix: 'بالترقية، فإنك توافق على',
    privacyPolicy: 'سياسة الخصوصية',
    and: 'و',
    termsOfService: 'شروط الخدمة',
    previousFeature: 'الميزة السابقة',
    nextFeature: 'الميزة التالية',
    featuresAria: 'ميزات Prime',
    featureTabAria: '{{title}} ({{index}} من {{total}})',
    valuePointStorage: '{{gb}} GB تخزين للمستندات والوسائط',
    valuePointNoAutoRenew: 'لا تجديد تلقائي — أنت تتحكم',
    valuePointSecureCheckout: 'دفع آمن عبر TAP',
    checkoutNoteTap: 'تتم معالجة الدفع بأمان عبر TAP.',
    checkoutNoteOneYear: 'اشتراكك يستمر لمدة سنة من تاريخ الشراء.',
    checkoutNoteNoAutoRenew: 'لا يوجد تجديد تلقائي — جدّد يدوياً عند انتهاء خطتك.',
    checkoutNoteActiveUntil: 'تظل الميزات المميزة نشطة حتى نهاية فترة الدفع.',
    featureStorageTitle: 'احصل على مساحة أكبر',
    featureStorageDescription: 'استمتع بـ 100 GB إضافية وشارك المزيد من المستندات.',
    featureStorageAlt: 'تميمة MyU مع مساحة تخزين إضافية',
    featureVideoTitle: 'فيديوهات أطول',
    featureVideoDescription: 'أضف حتى 30 دقيقة من الفيديو في كل منشور.',
    featureVideoAlt: 'تميمة MyU تسجّل فيديوهات أطول',
    featurePhotosTitle: 'أضف المزيد من الصور لمنشوراتك',
    featurePhotosDescription: 'أرفق حتى 20 صورة من معرضك في كل مرة.',
    featurePhotosAlt: 'تميمة MyU تضيف المزيد من الصور',
    featureClassesTitle: 'أنشئ المزيد من الصفوف',
    featureClassesDescription: 'أضف حتى 12 صفاً إضافياً إلى ملفك الشخصي.',
    featureClassesAlt: 'تميمة MyU تنشئ المزيد من الصفوف',
    featureInvisibleReaderTitle: 'قارئ غير مرئي',
    featureInvisibleReaderDescription: 'اقرأ رسائل الدردشة دون أن يعلم الطرف الآخر أنك رأيتها.',
    featureInvisibleReaderAlt: 'تميمة MyU مع قارئ دردشة غير مرئي',
    featureAdFreeTitle: 'تجربة بدون إعلانات',
    featureAdFreeDescription: 'استمتع باستخدام MyU دون رؤية أي إعلانات.',
    featureAdFreeAlt: 'تميمة MyU مع تجربة بدون إعلانات',
    featureExploreTitle: 'رتبة أعلى',
    featureExploreDescription:
      'سهّل على الآخرين العثور عليك في الاستكشاف بالظهور أعلى في كل قائمة.',
    featureExploreAlt: 'تميمة MyU بترتيب أعلى في الاستكشاف',
    featureAttendanceTitle: 'سجّل حضوراً غير محدود',
    featureAttendanceDescription: 'استمتع بجلسات حضور وتقارير غير محدودة لجميع صفوفك.',
    featureAttendanceAlt: 'تميمة MyU تسجّل حضوراً غير محدود',
    featureHistoryTitle: 'احتفظ بسجل رسائلك',
    featureHistoryDescription: 'لن تفقد السجلات السابقة، ويمكنك مراجعة الرسائل القديمة في أي وقت.',
    featureHistoryAlt: 'تميمة MyU تحفظ سجل الرسائل',
    featureMembersTitle: 'أرسل إلى المزيد من الأشخاص',
    featureMembersDescription: 'ارفع الحد الأقصى لحجم الصف من 500 إلى 5,000 عضو.',
    featureMembersAlt: 'تميمة MyU ترسل منشورات إلى المزيد',
    featureNetworkTitle: 'وسّع شبكتك',
    featureNetworkDescription: 'تواصل مع طلاب من مدارس أخرى ووسّع تأثيرك.',
    featureNetworkAlt: 'تميمة MyU توسّع شبكتها',
  },
  class: {
    emptyPostsTitle: 'لا توجد منشورات بعد',
    emptyPostsDescription: 'المنشورات المشاركة مع هذا الصف ستظهر هنا.',
    emptyMembersTitle: 'لا يوجد أعضاء بعد',
    emptyMembersDescription:
      'الطلاب والمعلّمون وأولياء الأمور والحسابات الأخرى المرتبطة بهذا الصف ستظهر هنا.',
    emptyRosterTitle: 'لا يوجد طلاب في القائمة',
    emptyRosterDescription: 'إدخالات قائمة الصف ستظهر هنا.',
    emptyAttendanceTitle: 'لا سجلات حضور',
    emptyAttendanceDescription: 'جلسات الحضور وسجله لهذا الصف ستظهر هنا.',
    emptyProgressTitle: 'لا تقارير تقدم',
    emptyProgressDescription: 'قوالب التقارير والتقدم المُقدّم لهذا الصف ستظهر هنا.',
    emptyBehaviourTitle: 'لا إدخالات سلوك',
    emptyBehaviourDescription: 'نقاط السلوك والنشاط لهذا الصف ستظهر هنا.',
    emptyGradesTitle: 'لم تُعدّ الدرجات',
    emptyGradesDescription: 'فترات التقييم والفئات والعناصر لهذا الصف ستظهر هنا.',
    emptyResourcesTitle: 'لا موارد',
    emptyResourcesDescription: 'الملفات والمواد المشاركة في هذا الصف ستظهر هنا.',
    sectionsNavAria: 'أقسام الصف',
    scrollTabsLeftAria: 'تمرير التبويبات لليسار',
    scrollTabsRightAria: 'تمرير التبويبات لليمين',
    tabPosts: 'المنشورات',
    tabMembers: 'الأعضاء',
    tabRoster: 'القائمة',
    tabAttendance: 'الحضور',
    tabProgress: 'التقدم',
    tabBehaviour: 'السلوك',
    tabGrades: 'الدرجات',
    tabCalendar: 'التقويم',
    tabResources: 'الموارد',
    formEditTitle: 'تعديل الصف',
    formCreateTitle: 'إنشاء صف',
    formCreateSubmit: 'إنشاء صف',
    formClassTypeLabel: 'نوع الصف',
    formSelectClassType: 'اختر نوع الصف',
    formClassNameLabel: 'اسم الصف',
    formCharactersRemaining: '{{count}} حرف متبقٍ',
    formClassNamePlaceholder: 'مثال: رياضيات 10 أ',
    formAcademicYearLabel: 'السنة الدراسية',
    formSelectAcademicYear: 'اختر السنة الدراسية',
    formSubjectLabel: 'المادة',
    formSubjectHint: 'اختياري — ابحث للعثور على مادة بسرعة',
    formSelectSubjectOptional: 'اختر المادة (اختياري)',
    formSearchSubjects: 'البحث في المواد…',
    formNoSubject: 'بدون مادة',
    formGradeLabel: 'السنة/الصف',
    formSelectGrade: 'اختر السنة أو الصف',
    formCreditsLabel: 'الساعات المعتمدة',
    formCreditsPlaceholder: 'مثال: 3',
    formPrivateLabel: 'صف خاص',
    formPrivateDescription: 'يمكن الوصول إلى الصف الخاص فقط من قبل الأعضاء المصرّح لهم.',
    formNotifyFollowersLabel: 'إشعار المتابعين',
    formNotifyFollowersDescription: 'إذا تم التحديد، سيتم إشعار المتابعين بإنشاء الصف.',
    formArchivedLabel: 'صف مؤرشف',
    formArchivedDescription:
      'هذا الصف مخفي من الشريط الجانبي وملفك الشخصي. انقله إلى ملفك الشخصي لتفعيله مجددًا، أو احذفه نهائيًا.',
    formRestoreClass: 'استعادة الصف',
    formMoveToProfile: 'نقل إلى الملف الشخصي',
    formDeleteClass: 'حذف الصف',
    formDangerZone: 'منطقة الخطر',
    formDangerZoneDescription:
      'الأرشفة تخفي هذا الصف من الشريط الجانبي وملفك الشخصي. يمكنك استعادته من الإعدادات. الحذف نهائي ولا يمكن التراجع عنه.',
    formArchiveClass: 'أرشفة الصف',
    validationSelectClassType: 'اختر نوع الصف.',
    validationEnterClassName: 'أدخل اسم الصف.',
    validationClassNameMaxLength: 'يجب أن يكون اسم الصف {{max}} حرفًا أو أقل.',
    validationSelectAcademicYear: 'اختر سنة دراسية.',
    validationSelectGrade: 'اختر سنة أو صف.',
    validationInvalidCredits: 'يجب أن تكون الساعات المعتمدة رقمًا صالحًا (0 أو أكثر).',
    typeHomeroom: 'صف/فصل',
    typeSubject: 'صف مادة',
    typeCommunication: 'قناة تواصل',
    typeSubmodule: 'وحدة فرعية',
    shareTitle: 'مشاركة الصف',
    shareCodeLabel: 'الرمز: {{code}}',
    shareQrAria: 'رمز QR للانضمام إلى {{className}}',
    shareCopyLink: 'نسخ رابط الصف',
    shareCopyCode: 'نسخ رمز الصف',
    membersAddModalAria: 'إضافة أعضاء للصف',
    membersAddModalTitle: 'إضافة أعضاء',
    membersSearchPlaceholder: 'البحث بالاسم أو اسم المستخدم…',
    membersSearchAria: 'البحث عن مستخدمين',
    membersShownCount: '{{shown}} من {{total}} معروض',
    membersLoadingPeople: 'جارٍ تحميل الأشخاص…',
    membersAllAlreadyAdded: 'جميع الأشخاص المتاحين موجودون بالفعل في هذا الصف.',
    membersAdding: 'جارٍ الإضافة…',
    membersAddToClass: 'إضافة إلى الصف',
    membersAddToClassWithCount: 'إضافة إلى الصف ({{count}})',
    roleOwner: 'مالك الصف',
    roleAdmin: 'مشرف الصف',
    membersFallbackName: 'عضو',
    membersSectionTitle: 'أعضاء الصف ({{count}})',
    membersAddMember: 'إضافة عضو',
    membersShareClass: 'مشاركة الصف',
    membersLoadError: 'فشل تحميل أعضاء الصف.',
    membersEmptyInline: 'لا يوجد أعضاء بعد.',
    membersCandidatesLoadError: 'فشل تحميل الأشخاص للإضافة.',
    adminRequestPending: 'طلب مشرف قيد الانتظار',
    cancelAdminRequest: 'إلغاء الطلب',
    removeAdmin: 'إزالة المشرف',
    makeAdmin: 'تعيين مشرف',
    removeFromClass: 'إزالة من الصف',
    memberActionsFor: 'إجراءات {{name}}',
    studentIdLabel: 'رقم الطالب {{id}}',
    rosterLinkAccountAria: 'ربط عضو الصف بطالب القائمة',
    rosterLinkAccountTitle: 'ربط حساب — {{studentName}}',
    rosterLinkAccountDescription:
      'اختر عضو صف لربطه بهذا الطالب في القائمة. يمكن إزالة الربط لاحقًا.',
    rosterAllMembersLinked: 'جميع أعضاء الصف مربوطون بالفعل.',
    rosterClassMembersListAria: 'أعضاء الصف',
    rosterLinkAccount: 'ربط حساب',
    rosterEditStudentAria: 'تعديل {{studentName}}',
    rosterEditStudentTitle: 'تعديل طالب القائمة',
    rosterStudentNameLabel: 'اسم الطالب',
    rosterNameRequired: 'اسم الطالب مطلوب.',
    rosterStudentIdLabel: 'رقم الطالب',
    rosterStudentIdPlaceholder: 'مثال: S12345',
    rosterAddStudentsAria: 'إضافة طلاب إلى القائمة',
    rosterAddStudentsTitle: 'إضافة طلاب',
    rosterMethodManual: 'إدخال يدوي',
    rosterMethodDirectory: 'دليل المؤسسة',
    rosterMethodMembers: 'أعضاء الصف',
    rosterMethodCsv: 'ملف CSV',
    rosterFullNamePlaceholder: 'الاسم الكامل',
    rosterDirectoryDescription: 'اختر طلابًا من دليل مؤسستك.',
    rosterSearchDirectoryPlaceholder: 'البحث بالاسم أو رقم الطالب…',
    rosterSearchDirectoryAria: 'البحث في دليل المؤسسة',
    rosterShownSelected: '{{shown}} من {{total}} معروض · {{selected}} محدد',
    rosterSelectAll: 'تحديد الكل',
    rosterDeselectAll: 'إلغاء تحديد الكل',
    rosterMembersDescription:
      'أضف أعضاء الصف الحاليين إلى القائمة (ينشئ إدخال قائمة من ملفهم الشخصي).',
    rosterSearchMembersPlaceholder: 'البحث بالاسم أو الرقم أو الدور…',
    rosterSearchMembersAria: 'البحث في أعضاء الصف',
    rosterFallbackMember: 'عضو',
    rosterCsvInstructions: 'ارفع ملف CSV، ثم اختر العمود الذي يحتوي على اسم الطالب.',
    rosterCsvFileLabel: 'ملف CSV',
    rosterCsvExcludeFirstRow: 'استثناء الصف الأول من الجدول',
    rosterCsvSelectNameColumn: 'اختر عمود اسم الطالب (إلزامي)',
    rosterCsvSelectIdColumn: 'اختر عمود رقم الطالب (اختياري)',
    rosterCsvReadyToImport: '{{fileName}} — {{count}} طالب(طلاب) جاهز(ون) للاستيراد',
    rosterCsvNoValidRows: 'لم يتم العثور على صفوف صالحة.',
    rosterCsvReadError: 'تعذّر قراءة الملف.',
    rosterCsvUploadFirst: 'ارفع ملف CSV أولاً.',
    rosterCsvPreview: 'معاينة التحديد',
    rosterCsvPreviewTitle: 'معاينة التحديد',
    rosterAddStudent: 'إضافة طالب',
    rosterImportStudents: 'استيراد طلاب',
    rosterImportStudentsCount: 'استيراد {{count}} طالب',
    rosterAddSelected: 'إضافة المحدد',
    rosterNoSearchMatches: 'لا توجد نتائج لبحثك.',
    rosterUnlinkTitle: 'إلغاء ربط الحساب',
    rosterUnlinkMessage:
      'إزالة الربط بين طالب القائمة {{rosterName}} وعضو الصف {{memberName}}؟ سيبقون في القائمة والصف بشكل منفصل.',
    rosterUnlinkConfirm: 'إلغاء ربط الحساب',
    rosterRemoveTitle: 'إزالة من القائمة',
    rosterRemoveMessage: 'إزالة {{name}} من قائمة الصف؟ قد تتأثر أعضاء الصف المرتبطون أيضًا.',
    rosterRemoveConfirm: 'إزالة الطالب',
    shareCodeCopied: 'تم نسخ الرمز',
    rosterTitle: 'قائمة الصف ({{count}})',
    rosterSortAria: 'ترتيب القائمة',
    rosterSortAz: 'ترتيب أ–ي',
    rosterSortZa: 'ترتيب ي–أ',
    rosterSortManual: 'ترتيب يدوي',
    rosterSortList: 'قائمة الترتيب',
    rosterEditStudent: 'تعديل الطالب',
    rosterRemoveFromRoster: 'إزالة من القائمة',
    rosterLinkedAccounts: 'الحسابات المرتبطة:',
    rosterLinkedNone: 'لا يوجد',
    rosterUnlinkMemberAria: 'إلغاء ربط {{name}}',
    rosterActionsForAria: 'إجراءات {{name}}',
    rosterStudentIdPrefix: 'المعرّف: {{id}}',
  },
  search: {
    placeholder: 'بحث…',
    resultTypeAria: 'نوع نتائج البحث',
    tabUsers: 'المستخدمون',
    tabClasses: 'الصفوف',
    usersLoadError: 'تعذّر تحميل النتائج. يرجى المحاولة مرة أخرى.',
    classesLoadError: 'تعذّر تحميل الصفوف. يرجى المحاولة مرة أخرى.',
    updating: 'جارٍ التحديث…',
    searching: 'جارٍ البحث…',
    noUsersFound: 'لم يُعثر على مستخدمين. جرّب اسمًا أو اسم مستخدم مختلفًا.',
    noClassesFound: 'لم يُعثر على صفوف. ابحث برمز الصف (مثل CS101).',
  },
  payments: {
    loading: 'جارٍ تحميل تفاصيل الدفع…',
    loadError: 'تعذّر تحميل تفاصيل الدفع. يرجى المحاولة مرة أخرى.',
    noPrograms: 'لم يتم العثور على برامج مسجّلة برسوم مستحقة.',
    noProducts: 'لا توجد منتجات متاحة حاليًا.',
    sectionsAria: 'أقسام الدفع',
    tabEnrolled: 'البرنامج المسجّل',
    tabProducts: 'منتجات أخرى',
    tabHistory: 'سجل المدفوعات',
    paymentSubmitted: 'تم الدفع بنجاح. تم تأكيد مرجع عملية الدفع.',
    paymentFailed: 'لم تكتمل عملية الدفع. تم الاحتفاظ بسلة التسوق لتتمكن من المحاولة مرة أخرى.',
    paymentPending: 'لا تزال عملية الدفع قيد التأكيد. يرجى التحقق مرة أخرى قريبًا.',
    productDateCannotBePast: 'يرجى اختيار تاريخ اليوم أو تاريخ لاحق.',
    historyLoading: 'جارٍ تحميل سجل المدفوعات…',
    historyLoadError: 'تعذّر تحميل سجل المدفوعات. يرجى المحاولة مرة أخرى.',
    historyEmpty: 'لا توجد مدفوعات مسجّلة لطلابك المرتبطين بعد.',
    historyMoreLines: '+{{count}} عناصر إضافية',
    downloadReceipt: 'تنزيل الإيصال',
    receiptPreparing: 'جارٍ تجهيز الإيصال…',
    receiptTitle: 'إيصال الدفع',
    receiptSubtitle: 'تأكيد رسمي بالدفع',
    receiptReference: 'المرجع',
    receiptDate: 'التاريخ',
    receiptAmount: 'المبلغ',
    receiptPaymentMode: 'طريقة الدفع',
    receiptSource: 'سجّل بواسطة',
    receiptSourceParent: 'عبر الإنترنت (ولي الأمر)',
    receiptSourceAdmin: 'مكتب المدرسة',
    receiptThankYou: 'شكرًا لك على الدفع.',
    receiptPrintedOn: 'تاريخ الطباعة',
    receiptReceived: 'مستلم',
    receiptBrand: 'MyU',
    filterByCategoryAria: 'تصفية حسب الفئة',
    filterByStudentAria: 'تصفية حسب الطالب',
    filterAll: 'الكل',
    backToCartAria: 'العودة إلى السلة',
    backToPaymentsAria: 'العودة إلى الدفع',
    assignToStudentTitle: 'تعيين لطالب',
    assignProductAria: 'تعيين {{productName}} للطلاب',
    includeItemAria: 'تضمين {{label}}',
    addForAnotherStudentAria: 'إضافة لطالب آخر',
    amountPlaceholder: 'المبلغ',
    customAmountAria: 'مبلغ مخصص لـ {{label}}',
    customAmountTooHigh: 'لا يمكن أن يتجاوز المبلغ {{amount}}',
    receiptDetails: 'التفاصيل',
    receiptStatus: 'الحالة',
    refundLabel: 'الاسترداد',
    walletRefundLabel: 'استرداد المحفظة',
    refundDetails: 'الاسترداد: {{status}}{{amount}}{{method}}',
    quantityLabel: 'الكمية: {{count}}',
    selectFeeAria: 'اختيار {{label}}',
    paid: 'مدفوع',
    partiallyPaid: 'مدفوع جزئيًا',
    paidAmount: 'المدفوع: {{amount}}',
    discountedTotal: 'بعد الخصم: {{amount}}',
    remainingDue: 'المستحق: {{amount}}',
    discountCode: 'الخصم: {{code}}',
    discountValue: 'خصم {{value}}',
    cartAria: 'سلة التسوق',
    cartWithItemsAria: 'سلة التسوق، {{count}} عناصر',
    shoppingCart: 'سلة التسوق',
    checkout: 'إتمام الدفع',
    nothingToCheckout: 'لا يوجد ما يمكن دفعه. أضف عناصر إلى سلة التسوق أولاً.',
    viewCart: 'عرض السلة',
    qty: 'الكمية',
    studentLabel: 'الطالب',
    cartEmpty: 'سلة التسوق فارغة. اختر رسومًا أو منتجات للمتابعة.',
    add: 'إضافة',
    orderSummary: 'ملخص الطلب',
    total: 'الإجمالي',
    proceedToCheckout: 'المتابعة للدفع',
    proceedToPayment: 'المتابعة للدفع',
    reviewOrder: 'راجع طلبك',
    paymentSecureNote: 'تتم معالجة الدفع بأمان عبر TAP. سيتم تحويلك لإكمال عملية الدفع.',
    openingPayment: 'جارٍ فتح الدفع الآمن…',
    paymentInitiationError: 'تعذّر بدء عملية الدفع. يرجى المحاولة مرة أخرى.',
    feeItem: 'البند',
    feeDueDate: 'تاريخ الاستحقاق',
    feeAmount: 'المبلغ',
    studentIdPrefix: 'المعرّف {{id}}',
    balanceForwardApplied: 'تم تطبيق رصيد المحفظة: {{amount}}',
    walletBalanceLabel: 'رصيد المحفظة',
    walletBalanceDescription: 'الرصيد المتاح بعد إزالة البرنامج والتعديلات الأخرى.',
    applyWallet: 'استخدام رصيد المحفظة',
    applyingWallet: 'جارٍ استخدام المحفظة…',
    walletAvailable: '{{amount}} متاح',
    walletApplyError: 'تعذّر استخدام رصيد المحفظة. يرجى المحاولة مرة أخرى.',
    cartPreviewTitle: 'سلتك',
    cartPreviewEmpty: 'لم تُضف أي عناصر بعد.',
    selectAllStudents: 'تحديد جميع الطلاب',
    unselectAllStudents: 'إلغاء تحديد جميع الطلاب',
    assignStudentsHint: 'اختر طالبًا واحدًا أو أكثر لـ {{productName}}.',
    assignAllAssigned: 'هذا العنصر مُعيَّن بالفعل لجميع الطلاب بهذه الخيارات.',
    selectDay: 'اختر اليوم',
    selectSize: 'اختر المقاس',
    size: 'المقاس',
    selectOptions: 'اختر الخيارات',
    requiredProductOption: 'هذا الحقل مطلوب.',
    addToCart: 'أضف إلى السلة',
    outOfStock: 'نفد المخزون',
    quantityAvailable: 'الحد الأقصى للكمية المتاحة: {{count}}',
    inventoryExceeded: 'الكمية المطلوبة للمنتج {{product}} لم تعد متوفرة.',
  },
  reportCards: {
    title: 'بطاقات التقرير',
    subtitle: 'اعرض وحمّل تقارير التقدّم للطلاب المرتبطين بحسابك.',
    documents: 'المستندات',
    filterByStudentAria: 'تصفية حسب الطالب',
    filterByYearAria: 'تصفية حسب السنة الدراسية',
    yearFallback: 'السنة',
    studentIdLabel: 'معرّف الطالب {{id}}',
    loading: 'جارٍ تحميل بطاقات التقرير…',
    loadErrorTitle: 'تعذّر تحميل بطاقات التقرير',
    loadErrorDescription: 'يرجى التحقق من الاتصال والمحاولة مرة أخرى.',
    noStudentsTitle: 'لا يوجد طلاب مرتبطون',
    noStudentsDescription: 'ستظهر بطاقات التقرير هنا عند ربط الطلاب بحساب ولي الأمر.',
    nothingTitle: 'لا يوجد ما يُعرض',
    pdfDocument: 'مستند PDF',
    noReportsForYear: 'لا تتوفر بطاقات تقرير لهذه السنة الدراسية.',
  },
  grades: {
    gradingTerms: 'فترات التقييم',
    addTerm: 'إضافة فترة',
    editTerm: 'تعديل الفترة',
    deleteTerm: 'حذف الفترة',
    deleteTermConfirm:
      'حذف {{name}}؟ سيؤدي ذلك إلى إزالة فترة التقييم وأوزان الفئات لهذا الصف، وقد تتأثر عناصر الدرجات في الفترة.',
    deletingTerm: 'جارٍ الحذف…',
    savingTerm: 'جارٍ الحفظ…',
    selectYear: 'السنة الدراسية',
    selectYearPlaceholder: 'اختر السنة',
    termTitleLabel: 'عنوان الفترة',
    termTitlePlaceholder: 'عنوان الفترة (مثال: الربع 1)',
    categoriesAndWeightage: 'فئات التقييم والأوزان',
    addCategory: 'إضافة فئة',
    addOrEditCategories: 'إضافة أو تعديل الفئات',
    selectGradingCategories: 'اختر فئات التقييم',
    selectCategoryFromList: 'يرجى اختيار فئة واحدة على الأقل من القائمة.',
    selectGradingCategory: 'يرجى اختيار فئة تقييم واحدة على الأقل.',
    missingInfo: 'معلومات ناقصة',
    categoryWeightageEmpty: 'لا يمكن أن يكون وزن الفئة فارغًا.',
    categoryWeightageMustEqual100: 'يجب أن يساوي مجموع أوزان الفئات 100.',
    weightTotal: 'المجموع: {{total}} / 100',
    categoryWeightFor: 'الوزن لـ {{name}}',
    noCategoriesSelected: 'لم يتم اختيار فئات بعد.',
    loadingTerms: 'جارٍ تحميل فترات التقييم…',
    loadTermsError: 'تعذّر تحميل فترات التقييم. يتم عرض بيانات المعاينة.',
    noTermsYet: 'لا توجد فترات تقييم بعد.',
    loadingGradeItems: 'جارٍ تحميل عناصر الدرجات…',
    loadGradeItemsError: 'تعذّر تحميل عناصر الدرجات. يتم عرض بيانات المعاينة.',
    loadingCategories: 'جارٍ تحميل الفئات…',
    noCategoriesInTerm: 'لا توجد فئات مهيّأة لهذه الفترة.',
    termActionsFor: 'إجراءات {{name}}',
    report: 'تقرير',
    generatingReport: 'جارٍ الإنشاء…',
    viewReport: 'عرض التقرير',
    emailReport: 'إرسال التقرير',
    emailingReport: 'جارٍ الإرسال…',
    reportDialogTitle: 'تقرير الدرجات',
    reportDialogAria: 'خيارات تقرير الدرجات',
    reportDialogMessage: 'كيف تريد عرض تقرير الدرجات؟',
    emailReportConfirmTitle: 'إرسال تقرير الدرجات بالبريد',
    emailReportConfirmMessage:
      'هل تريد استلام تقرير الدرجات الكامل لهذا الصف على بريدك الإلكتروني؟',
    reportEmailed: 'تم إرسال التقرير!',
    reportEmailError: 'تعذّر إرسال تقرير الدرجات. يرجى المحاولة مرة أخرى.',
    viewGradeItem: 'تفاصيل الدرجة',
    editGradeItem: 'تعديل الدرجة',
    permissionDeniedTitle: 'هذا الإجراء غير مسموح',
    permissionDeniedEditMessage:
      'ليس لديك إذن لتنفيذ هذا الإجراء. يرجى التواصل مع مسؤول المدرسة إذا كنت بحاجة إلى الوصول.',
    addGradeItem: 'إضافة عنوان درجة',
    deleteGrade: 'حذف التقييم',
    deleteGradeConfirm: 'حذف التقييم',
    deleting: 'جارٍ الحذف…',
    loadGradeDetailsError: 'تعذّر تحميل تفاصيل التقييم. يرجى المحاولة مرة أخرى.',
    itemTitle: 'عنوان الدرجة',
    fullGrade: 'الدرجة الكاملة',
    itemNamePlaceholder: 'مثال: اختبار 1، امتحان 1',
    maxGradePlaceholder: 'مثال: 100',
    studentGrades: 'درجات الطلاب',
    loadingStudents: 'جارٍ تحميل الطلاب…',
    noStudentsEnrolled: 'لا يوجد طلاب مسجّلون.',
    gradeLabel: 'الدرجة',
    noteLabel: 'ملاحظة',
    gradePlaceholder: '0',
    addNoteOptional: 'أضف ملاحظة (اختياري)',
    closeEditorAria: 'إغلاق المحرر',
    locked: 'مقفل',
    pendingAdminApproval: 'بانتظار موافقة المسؤول',
    fullMark: 'الدرجة الكاملة ({{count}})',
    averageGrade: 'متوسط الدرجة ({{value}}%)',
    totalMark: 'مجموع الدرجة ({{count}})',
    weight: 'الوزن ({{value}}%)',
    termCategorySummary: '{{count}} فئات تقييم •',
    studentGradesEmpty: 'عند نشر درجتك، ستظهر هنا.',
    studentGradesEmptyTitle: 'لا توجد درجات بعد',
    studentGradesUnlinkedTitle: 'الحساب غير مرتبط',
    studentGradesUnlinkedDescription:
      'لعرض درجاتك، اطلب من مالك الصف ربط حسابك باسمك في قائمة الحضور.',
    studentGradesErrorHint: 'يرجى المحاولة مرة أخرى لاحقًا.',
    loadCategoriesError: 'تعذّر تحميل الفئات. يرجى المحاولة مرة أخرى.',
  },
  behaviour: {
    title: 'نقاط السلوك',
    managePoints: 'إدارة النقاط',
    loading: 'جارٍ تحميل نقاط السلوك…',
    loadError: 'تعذّر تحميل نقاط السلوك.',
    selectStudentOrTeam: 'اختر طالبًا أو فريقًا',
    selectCategory: 'اختر فئة',
    saving: 'جارٍ الحفظ…',
    save: 'حفظ',
    select: 'اختيار',
    selected: 'محدد',
    searchTeamsPlaceholder: 'بحث في الفرق…',
    searchStudentsPlaceholder: 'بحث في الطلاب…',
    searchTeamsAria: 'بحث في الفرق',
    searchStudentsAria: 'بحث في الطلاب',
    noTeamsConfigured: 'لا توجد فرق مُعدّة لهذا الصف.',
    noTeamsMatch: 'لا توجد فرق مطابقة لبحثك.',
    noStudentsEnrolled: 'لا يوجد طلاب مسجّلون في هذا الصف.',
    noStudentsMatch: 'لا يوجد طلاب مطابقون لبحثك.',
    noCategoriesAvailable: 'لا توجد فئات متاحة.',
    noCategoryTab: 'لا توجد فئات {{tab}}.',
    managePointsSubtitle: 'منح أو خصم نقاط للطلاب أو الفرق',
    pointsToAward: 'النقاط المراد منحها',
    closeManagePointsAria: 'إغلاق إدارة النقاط',
    addCommentsPlaceholder: 'أضف تعليقات (اختياري)',
    closePickerAria: 'إغلاق المُنتقي',
    selectTeamsOrStudentsAria: 'اختر الفرق أو الطلاب',
    teamsShownCount: '{{shown}} من {{total}} فريق',
    studentsShownCount: '{{shown}} من {{total}} طالب',
    selectedCountSuffix: ' · {{count}} محدد',
    loadStudentsTeamsError: 'تعذّر تحميل الطلاب والفرق. يرجى المحاولة مرة أخرى.',
    categoryTypeAria: 'نوع الفئة',
    loadCategoriesError: 'تعذّر تحميل الفئات. يرجى المحاولة مرة أخرى.',
    categoryTabPositive: 'إيجابي',
    categoryTabNegative: 'سلبي',
    loadingPoints: 'جارٍ تحميل النقاط…',
    loadPointsError: 'تعذّر تحميل النقاط.',
    noPointsRecorded: 'لم تُسجّل أي نقاط بعد',
    teamLabel: 'الفريق:',
  },
  attendance: {
    takeAttendance: 'تسجيل الحضور',
    newAttendance: 'حضور جديد',
    hideCodeAria: 'إخفاء رمز الحضور',
    showCodeAria: 'إظهار رمز الحضور',
    useDefaultWindow: 'استخدام نافذة الوقت الافتراضية',
    useCurrentTime: 'استخدام الوقت الحالي',
    saving: 'جارٍ الحفظ…',
    save: 'حفظ',
    deleting: 'جارٍ الحذف…',
    delete: 'حذف',
    editDateTime: 'تعديل التاريخ والوقت',
    editAttendanceConfirmTitle: 'تعديل الحضور',
    editAttendanceConfirmMessage:
      'ستؤدي التغييرات على الإطار الزمني للحضور إلى إعادة ضبط حسابات وقت التأخر للطلاب. سيظل الطلاب المحددون كمتأخرين مسجلين كمتأخرين.',
    deleteAttendance: 'حذف الحضور',
    updateDateTimeError: 'تعذّر تحديث التاريخ والوقت. يرجى المحاولة مرة أخرى.',
    loadingPeriods: 'جارٍ تحميل الحصص…',
    periodsUnavailable: 'الحصص غير متاحة',
    noPeriods: 'لا توجد حصص',
    selectPeriod: 'اختر حصة',
    addNotePlaceholder: 'أضف ملاحظة…',
    moreOptionsNoneAria: 'المزيد من الخيارات (لا يوجد)',
    timeSelectionModeAria: 'وضع اختيار الوقت',
    periodAria: 'الحصة',
    timeRangeReadOnlyAria: 'نطاق الوقت (للقراءة فقط)',
    present: 'حاضر',
    late: 'متأخر',
    excused: 'معذور',
    absent: 'غائب',
    entryTime: 'وقت الدخول',
    minutesLate: 'دقائق التأخير',
    entryTimePlaceholder: '10:15 AM',
    minutesLatePlaceholder: '15',
    entryTimeOutsideWindow: 'يجب أن يكون وقت الدخول ضمن نطاق الحضور ({{window}}).',
    minutesLateInvalid: 'يجب أن يكون عدد دقائق التأخر رقمًا موجبًا.',
    futureDateNotAllowed: 'لا يمكن تعيين الحضور لتاريخ مستقبلي.',
    statPresent: 'حاضر ({{count}})',
    statLate: 'متأخر ({{count}})',
    statAbsent: 'غائب ({{count}})',
    statExcused: 'معذور ({{count}})',
    statTotalPresence: 'إجمالي الحضور ({{count}})',
    statTotalAbsence: 'إجمالي الغياب ({{count}})',
    actionsForAttendanceAria: 'إجراءات الحضور في {{date}}',
    attendanceDateAria: 'تاريخ الحضور',
    startTimeAria: 'وقت البداية',
    endTimeAria: 'وقت النهاية',
    selectPeriodLabel: 'اختر حصة',
    loadingAttendance: 'جارٍ تحميل الحضور…',
    deleteAttendanceConfirmTitle: 'حذف الحضور؟',
    deleteAttendanceConfirmAria: 'حذف الحضور',
    deleteAttendanceMessage: 'سيؤدي هذا إلى إزالة سجل الحضور نهائيًا لـ {{date}}{{periodSuffix}}.',
    deleteAttendancePeriodSuffix: ' ({{period}})',
    attendanceHistory: 'سجل الحضور',
    activeAttendance: 'الحضور النشط',
    loadAttendanceError: 'تعذّر تحميل الحضور. يرجى المحاولة مرة أخرى.',
    confirmDeleteAttendanceAria: 'تأكيد حذف الحضور',
    deleteAttendanceSessionMessage:
      'سيؤدي هذا إلى إزالة جلسة الحضور نهائيًا{{dateSuffix}}{{timeSuffix}}. لا يمكن التراجع عن هذا الإجراء.',
    deleteAttendanceDateSuffix: ' لـ {{date}}',
    deleteAttendanceTimeSuffix: ' ({{time}})',
    report: 'تقرير',
    generatingReport: 'جارٍ الإنشاء…',
    viewReport: 'عرض التقرير',
    emailReport: 'إرسال التقرير بالبريد',
    emailingReport: 'جارٍ الإرسال…',
    reportEmailed: 'تم إرسال التقرير',
    reportError: 'تعذّر إنشاء التقرير. يرجى المحاولة مرة أخرى.',
    reportDialogTitle: 'تقرير الحضور',
    reportDialogAria: 'خيارات تقرير الحضور',
    reportDialogMessage: 'كيف تريد عرض تقرير الحضور؟',
    emailReportConfirmTitle: 'إرسال تقرير الحضور بالبريد',
    emailReportConfirmMessage: 'سيتم إرسال تقرير الحضور الكامل إلى بريدك الإلكتروني كمرفق.',
    myAttendance: 'حضوري',
    acceptAttendance: 'قبول الحضور',
    acceptAttendanceTitle: 'قبول الحضور',
    activeAttendanceHint: 'معلمك يسجل الحضور الآن',
    markedPresent: 'تم تسجيلك حاضرًا',
    markedLate: 'تم تسجيلك متأخرًا',
    markedLateMinutes: 'تم تسجيلك متأخرًا ({{count}} دقيقة)',
    markedAbsent: 'تم تسجيلك غائبًا',
    markedExcused: 'تم تسجيلك معذورًا',
    loadingStudentAttendance: 'جارٍ تحميل حضورك…',
    loadStudentAttendanceError: 'تعذّر تحميل حضورك',
    loadStudentAttendanceErrorHint: 'يرجى المحاولة مرة أخرى بعد قليل.',
    studentAttendanceEmptyLinkedTitle: 'لا يوجد حضور بعد',
    studentAttendanceEmptyLinkedDescription: 'عندما يسجل معلمك الحضور ستظهر حالتك هنا.',
    studentAttendanceEmptyUnlinkedTitle: 'الحساب غير مرتبط',
    studentAttendanceEmptyUnlinkedDescription:
      'لعرض حضورك ودرجاتك وقبول الحضور التلقائي، اطلب من مالك الصف ربط حسابك باسمك في القائمة.',
    scanQrCode: 'امسح رمز QR',
    enterAttendanceCode: 'أو أدخل الرمز المكوّن من 6 أرقام',
    enterAttendanceCodeAria: 'أدخل رمز الحضور المكوّن من 6 أرقام',
    orEnterCode: 'أو',
    codeDigitsOnly: 'يُسمح بالأرقام فقط',
    validatingCode: 'جارٍ التحقق من الرمز…',
    validateCodeError: 'رمز غير صالح. يرجى المحاولة مرة أخرى.',
    acceptSuccessTitle: 'تم قبول الحضور',
    acceptSuccessMessage: 'تم تسجيلك حاضرًا',
    qrScannerUnsupported: 'مسح QR غير مدعوم في هذا المتصفح. أدخل الرمز يدويًا.',
    qrScannerDenied: 'يلزم الوصول إلى الكاميرا لمسح رمز QR.',
    qrScannerError: 'تعذّر تشغيل الكاميرا. أدخل الرمز يدويًا.',
    qrScannerRetry: 'حاول مرة أخرى',
  },
  resources: {
    title: 'موارد الصف',
    uploadResource: 'رفع مورد',
    addNewSection: 'إضافة قسم جديد',
    loading: 'جارٍ تحميل الموارد…',
    filterByTypeAria: 'تصفية الموارد حسب نوع الملف',
    engagementDetails: 'تفاصيل التفاعل',
    totalOpens: 'إجمالي الفتحات',
    uniqueStudents: 'طلاب فريدون',
    avgViewed: 'متوسط المشاهدة',
    lastOpened: 'آخر فتح: {{date}}',
    opens: 'فتحات',
    viewed: 'مشاهدة',
    showStats: 'عرض الإحصائيات',
    sort: 'ترتيب',
    moveToAnotherClass: 'نقل إلى صف آخر',
    delete: 'حذف',
    addResource: 'إضافة مورد',
    sectionNamePlaceholder: 'اسم القسم',
    saveSectionAria: 'حفظ القسم',
    cancelSectionAria: 'إلغاء',
    renameSection: 'إعادة التسمية',
    moveSectionUp: 'تحريك لأعلى',
    moveSectionDown: 'تحريك لأسفل',
    deleteSection: 'حذف القسم',
    sortFiles: 'ترتيب الملفات',
    confirmDeleteSection: 'حذف القسم؟',
    confirmDeleteSectionMessage:
      'سيؤدي هذا إلى حذف "{{name}}" وجميع موارده نهائيًا. لا يمكن التراجع عن هذا الإجراء.',
    confirmDeleteResource: 'حذف المورد؟',
    confirmDeleteResourceMessage:
      'سيؤدي هذا إلى حذف "{{name}}" نهائيًا. لا يمكن التراجع عن هذا الإجراء.',
    moveResourceTitle: 'نقل إلى قسم',
    moveResourceLabel: 'اختر قسمًا لنقل "{{name}}" إليه:',
    moveUp: 'تحريك لأعلى',
    moveDown: 'تحريك لأسفل',
    moveToSection: 'نقل إلى قسم…',
    deleteResource: 'حذف',
    uploadTitle: 'رفع مورد',
    uploadDropZone: 'اسحب الملف وأفلته هنا، أو',
    uploadBrowse: 'تصفّح',
    uploadSupportedTypes: 'PDF، Word، Excel، PowerPoint، فيديو',
    uploadSectionLabel: 'إضافة إلى قسم',
    uploadButton: 'رفع',
    uploading: 'جارٍ الرفع…',
    uploadSuccess: 'تم رفع الملف بنجاح',
    uploadError: 'فشل الرفع. يرجى المحاولة مرة أخرى.',
    selectFile: 'اختر ملفًا',
    noSections: 'لا توجد أقسام بعد',
    emptySection: 'لا توجد ملفات في هذا القسم',
  },
  progress: {
    reportTemplates: 'قوالب التقارير',
    loadingTemplates: 'جارٍ تحميل قوالب التقارير…',
    loadTemplatesError: 'تعذّر تحميل قوالب التقارير. يرجى المحاولة مرة أخرى.',
    noTemplates: 'لا توجد قوالب تقارير بعد.',
    selectStudent: 'اختر طالبًا',
    backToReportsAria: 'العودة إلى التقارير',
    backToTemplatesAria: 'العودة إلى القوالب',
    backToStudentListAria: 'العودة إلى قائمة الطلاب',
    backToReportAria: 'العودة إلى التقرير',
    previousStudentAria: 'الطالب السابق',
    nextStudentAria: 'الطالب التالي',
    reportActionsAria: 'إجراءات التقرير',
    reportActionsFor: 'إجراءات {{label}}',
    deleteReport: 'حذف التقرير',
    enterTextPlaceholder: 'أدخل نصًا…',
    enterNumberPlaceholder: 'أدخل رقمًا…',
    addNotePlaceholder: 'أضف ملاحظة…',
    submitted: 'مُرسَل',
    incomplete: 'غير مكتمل',
    copyToAll: 'نسخ للجميع',
    loadingStudents: 'جارٍ تحميل الطلاب…',
    loadingReports: 'جارٍ تحميل التقارير…',
    loadStudentsError: 'تعذّر تحميل الطلاب. يرجى المحاولة مرة أخرى.',
    noStudentsInReport: 'لا يوجد طلاب في هذا التقرير.',
    studentsIncluded: '{{included}}/{{total}} طالبًا مُدرَجًا',
    submitNewReport: 'إرسال تقرير جديد',
    submissionPeriod: 'فترة التسليم',
    loadReportsError: 'تعذّر تحميل التقارير. يرجى المحاولة مرة أخرى.',
    noReportsSubmitted: 'لم يُرسَل أي تقرير بعد.',
    loadingResponse: 'جارٍ تحميل الرد…',
    loadResponseError: 'تعذّر تحميل الرد. يرجى المحاولة مرة أخرى.',
    noResponseAvailable: 'لا يوجد رد متاح.',
  },
  questionResponses: {
    findAndReview: 'بحث ومراجعة',
    recipient: 'المستلم',
    filterByStatusAria: 'تصفية الردود حسب الحالة',
    viewingOneRecipient: 'عرض مستلم واحد',
    reviewStatus: 'حالة المراجعة',
    recipientFilterHint:
      'تُطبَّق مرشّحات حالة المراجعة عند تصفّح جميع المستلمين. لكل شخص ردّ واحد — افتحه بالاسم أعلاه.',
    viewAll: 'عرض الكل',
    searchRecipientsAria: 'بحث في المستلمين',
    searchRecipientsPlaceholder: 'بحث في {{count}} مستلم…',
    noResponsesSubmitted: 'لم يُرسَل أي رد بعد.',
    recipientSearchHint:
      'ابحث بالاسم لفتح ردّ واحد. استخدم حالة المراجعة أدناه للتصفّح حسب التقدّم لدى الجميع.',
    needsReview: 'يحتاج مراجعة',
    graded: 'مُقيَّم',
    feedbackProvided: 'تم تقديم ملاحظات',
    gradedFeedback: 'مُقيَّم · ملاحظات',
    rejected: 'مرفوض',
    filterAll: 'الكل',
  },
  globalCalendar: {
    previousMonthAria: 'الشهر السابق',
    nextMonthAria: 'الشهر التالي',
    selectMonthAria: 'اختر الشهر',
    selectYearAria: 'اختر السنة',
    selectDatePrompt: 'اختر تاريخًا',
    noEventsScheduled: 'لا توجد أحداث مجدولة',
    allClasses: 'جميع الصفوف',
  },
  push: {
    promptRegionAria: 'تفعيل الإشعارات',
    promptTitle: 'ابقَ على اطلاع',
    promptBody: 'فعّل إشعارات المتصفح للحصول على تحديثات الصف والرسائل فور حدوثها.',
    promptEnable: 'تفعيل الإشعارات',
    promptDismiss: 'ليس الآن',
    promptError: 'تعذّر تفعيل الإشعارات. تحقق من أذونات الموقع في المتصفح ثم حاول مجددًا.',
    toastRegionAria: 'الإشعارات',
    toastDismissAria: 'تجاهل الإشعار',
    gateEnabled: 'تم تفعيل الإشعارات',
    gateBlockedTitle: 'الإشعارات محظورة',
    gateBlockedBody:
      'يحتاج MyU إلى إذن لإرسال الإشعارات قبل تسجيل الدخول. انقر على أيقونة القفل في شريط عنوان المتصفح واضبط الإشعارات على السماح، ثم أعد تحميل هذه الصفحة.',
    gateRequiredTitle: 'الإشعارات مطلوبة',
    gateRequiredBody: 'فعّل إشعارات المتصفح للمتابعة.',
    gateRequesting: 'جارٍ الطلب…',
    gateEnable: 'تفعيل الإشعارات',
    gateContinueAria: 'فعّل الإشعارات للمتابعة',
  },
  tutorPackages: {
    title: 'الباقات',
    loading: 'جارٍ تحميل الباقات…',
    loadError: 'تعذر تحميل الباقات حالياً.',
    emptyTitle: 'لا توجد باقات بعد',
    emptyDescription: 'لم ينشر هذا المدرّس أي باقات صفوف مدفوعة.',
    priceValidity: '{{price}} · {{days}} يوم',
    subscribed: 'مشترك',
    subscribe: 'اشترك',
    selectPackage: 'اختيار',
    subscribing: 'جارٍ الاشتراك…',
    subscribeModalTitle: 'الاشتراك في الباقة',
    subscribeModalCancel: 'إلغاء',
    couponLabel: 'رمز العرض',
    couponPlaceholder: 'أدخل رمز العرض',
    couponApply: 'تطبيق',
    couponApplying: 'جاري التطبيق…',
    couponApplied: 'تم تطبيق العرض {{code}} (−{{value}})',
    couponRequired: 'أدخل رمز عرض للتطبيق.',
    couponInvalid: 'رمز العرض غير صالح.',
    applyPromo: 'هل لديك رمز عرض؟',
    checkoutError: 'تعذر بدء الدفع. حاول مرة أخرى.',
    checkoutOpened: 'فُتحت صفحة الدفع في تبويب جديد. أكمل الدفع للانضمام إلى الصفوف.',
    freeSubscribed: 'تم الاشتراك. ستظهر صفوفك قريباً.',
    confirmationLoading: 'جارٍ تحميل ملخص الطلب…',
    confirmationErrorTitle: 'الطلب غير موجود',
    confirmationErrorDescription:
      'لم نعثر على مرجع الدفع هذا. إذا دفعت للتو، انتظر قليلاً ثم حدّث الصفحة.',
    confirmationSuccessTitle: 'تم الدفع بنجاح',
    confirmationSuccessDescription: 'اكتمل شراء باقة المدرّس. تُفتح الصفوف المرتبطة تلقائياً.',
    confirmationPendingTitle: 'حالة الدفع',
    confirmationPendingDescription: 'الحالة الحالية: {{status}}.',
    packageLabel: 'الباقة',
    amountLabel: 'المبلغ',
    validityLabel: 'الصلاحية',
    validityDays: '{{days}} يوم',
    referenceLabel: 'المرجع',
    viewTutorProfile: 'عرض ملف المدرّس',
    backToFeed: 'العودة إلى الخلاصة',
    exploreTitle: 'المدرّسون',
    recommendedTitle: 'موصى به لك',
    subscriptionPageEyebrow: 'باقات المدرّس',
    subscriptionPageTitle: '@{{username}}',
    subscriptionPageDescription: 'اشترك في باقات الصفوف المدفوعة لهذا المدرّس.',
    subscriptionPageMissingUsername: 'لم يتم توفير اسم مستخدم للمدرّس.',
    backToProfile: 'العودة إلى الملف الشخصي',
    directoryTitle: 'المدرّسون',
    directoryDescription: 'تصفّح المدرّسين وافتح باقات اشتراكهم.',
    directoryLoadError: 'تعذر تحميل المدرّسين حالياً.',
    directoryEmptyTitle: 'لا يوجد مدرّسون بعد',
    directoryEmptyDescription: 'سيظهر المدرّسون هنا عند توفر الحسابات.',
    viewPackages: 'الباقات',
    loadMoreTutors: 'تحميل المزيد',
  },
  admissions: {
    loading: 'جارٍ التحميل…',
    formUnavailable: 'نموذج التقديم غير متاح.',
    subtitle: 'أكمل كل خطوة لإرسال طلبك.',
    progress: 'تقدم الطلب',
    registrationFee: 'رسوم التسجيل',
    requiredFields: 'يرجى إكمال جميع الحقول المطلوبة.',
    select: 'اختر…',
    grade: 'الصف',
    enrollmentYears: 'سنوات التسجيل',
    reviewTitle: 'راجع طلبك',
    applicantName: 'اسم المتقدم',
    paymentNotice: 'سيُطلب منك إكمال دفع رسوم التسجيل بعد الإرسال.',
    documentsNotice: 'يمكنك رفع المستندات المطلوبة بعد الإرسال.',
    back: 'رجوع',
    next: 'التالي',
    submitting: 'جارٍ الإرسال…',
    submit: 'إرسال الطلب',
    submitAndPay: 'إرسال ودفع',
    submitSuccess: 'تم إرسال طلبك بنجاح.',
    paymentSuccess: 'تم الدفع بنجاح.',
    paymentFailed: 'لم تكتمل عملية الدفع.',
    paymentStartFailed: 'تعذر بدء الدفع. يرجى المحاولة مرة أخرى.',
    uploadDocuments: 'رفع المستندات',
    backToForm: 'العودة إلى الطلب',
    documentsUnavailable: 'هذه المستندات غير متاحة.',
    documentProgress: 'تم رفع {{uploaded}} من {{total}} مستندات مطلوبة',
    documentsComplete: 'تم رفع جميع المستندات المطلوبة.',
    viewDocument: 'عرض المستند المرفوع',
    notUploaded: 'لم يتم الرفع بعد',
    uploading: 'جارٍ الرفع…',
    chooseFile: 'اختر ملفاً',
    finish: 'إنهاء',
    steps: {
      basic: 'الطالب',
      grade: 'الصف',
      contact: 'التواصل',
      medical: 'الطبي',
      custom: 'إضافي',
      review: 'المراجعة',
    },
    fields: {
      first_name: 'الاسم الأول',
      last_name: 'اسم العائلة',
      arabic_name: 'الاسم بالعربية',
      government_id: 'الرقم المدني',
      dob: 'تاريخ الميلاد',
      gender: 'الجنس',
      nationality: 'الجنسية',
      religion: 'الديانة',
      father_full_name: 'اسم الأب الكامل',
      father_civil_id: 'الرقم المدني للأب',
      father_nationality: 'جنسية الأب',
      mother_full_name: 'اسم الأم الكامل',
      mother_civil_id: 'الرقم المدني للأم',
      mother_nationality: 'جنسية الأم',
      other_note: 'ملاحظات أخرى',
      father_email: 'بريد الأب',
      father_mobile: 'هاتف الأب',
      mother_email: 'بريد الأم',
      mother_mobile: 'هاتف الأم',
      contact_person: 'جهة الاتصال',
      contact_person_relation: 'صلة القرابة',
      contact_number: 'رقم الاتصال',
      contact_email: 'بريد الاتصال',
      home_address: 'عنوان المنزل',
      medical_condition: 'الحالة الطبية',
      learning_disabilities: 'صعوبات التعلم',
      food_allergies: 'حساسية الطعام',
      special_needs: 'الاحتياجات الخاصة',
    },
  },
};
